import {
  AgentStatus,
  CallStatus,
  CaseStatus,
  HotelCategories,
  SALE_ORDER_STATUS,
  WhatsappMessageEventStatusType,
} from '@bandido/shared/constants';
import {
  CommunicationType,
  IVRActionType,
  MarkupFieldOptionHotels,
  MarkupRuleOperator,
  NotificationType,
  OriginatedByChannel,
  TimeoutAction,
  UserRoles,
} from '@bandido/shared/types';
import { WELCOME_MESSAGE } from '@frontend/features/calls-menu/constants';

export const espanol = {
  filters: {
    filterBy: 'Filter by',
    clear: 'Limpiar',
    clearAll: 'Limpiar todo',
    namePlaceholder: 'Escribe un nombre',
    nameLabel: 'Nombre del filtro',
    isPublic: 'Publico (todos podran verlo)',
    createFilter: 'Crear Filtro',
    filterList: 'Lista de filtros',
    editFilter: 'Editar Filtro',
    deleteFilter: 'Eliminar Filtro',
    public: 'Públicos',
    private: 'Privados',
    createdSuccess: 'Filtro guardado',
    createdError: 'Hubo un error al guardar el filtro',
    deletedSuccess: 'Filtro eliminado exitosamente',
    deletedError: 'Hubo un error al eliminar el filtro',
    deleteConfirmTitle: 'Eliminar Filtro',
    deleteConfirmMessage: '¿Estás seguro de que quieres eliminar este filtro? Esta acción no se puede deshacer.',
    priceRange: 'Rango de precios',
    searchName: 'Buscar por nombre',
    hotelChains: 'Cadenas',
    categories: 'Categorias',
    amenities: 'Comodidades',
    date: {
      today: 'Hoy',
      yesterday: 'Ayer',
      thisWeek: 'Esta semana',
      thisMonth: 'Este mes',
      lastWeek: 'La semana pasada',
      lastMonth: 'El mes pasado',
      lastNDays: 'Últimos N días',
      tomorrow: 'Mañana',
      nextWeek: 'Próxima semana',
      nextMonth: 'Próximo mes',
      nextNDays: 'Próximos N días',
      specificDate: 'Fecha específica',
      dateRange: 'Rango de fechas',
      numberOfDays: 'Número de días',
      from: 'Desde',
      to: 'Hasta',
      selectDateType: 'Seleccionar tipo de fecha',
    },
    clientName: 'Nombre del Cliente',
    countries: 'Países',
    agencies: 'Agencias',
    preferredAgent: 'Agente Preferido',
    documentTypes: 'Tipos de Documento',
    searchClientNamePlaceholder: 'Buscar por nombre o apellido',
    myClients: 'Ver solo mis clientes',
    myCases: 'Ver solo mis casos',
  },
  userNotificationTriggers: {
    title: 'Gestión de Notificaciones',
    createTrigger: 'Crear Disparador',
    editTrigger: 'Editar Disparador',
    deleteTrigger: 'Eliminar Disparador',
    trigger: 'Disparador',
    active: 'Activo',
    noConditions: 'Sin condiciones específicas',
    client: 'Cliente',
    clientOptional: 'Cliente (opcional)',
    agent: 'Agente',
    agentOptional: 'Agente (opcional)',
    workflow: 'Workflow',
    step: 'Paso',
    steps: 'pasos',
    status: 'Estado',
    case: 'Caso',
    specificCase: 'Caso específico (opcional)',
    addWorkflow: 'Agregar workflow',
    addWorkflowHint: 'Selecciona un workflow y al menos un paso para agregar otra fila.',
    removeWorkflow: 'Eliminar workflow',
    selectClient: 'Seleccionar cliente',
    selectAgent: 'Seleccionar agente',
    selectWorkflow: 'Seleccionar workflow(s)',
    selectStep: 'Seleccionar paso',
    selectStatus: 'Seleccionar estado',
    selectWorkflowFirst: 'Primero selecciona un workflow',
    caseIdPlaceholder: 'ID del caso',
    allClients: 'Todos los clientes',
    allAgents: 'Todos los agentes',
    anyStep: 'Cualquier paso',
    allWorkflows: 'Todos los workflows',
    anyStatus: 'Cualquier estado',
    anyCase: 'Cualquier caso',
    formDescription:
      'Configura las condiciones que activarán una notificación. Al menos una condición debe ser especificada.',
    noTriggers: 'No hay disparadores configurados',
    noTriggersDescription: 'Crea tu primer disparador para recibir notificaciones automáticas.',
    createFirst: 'Crear primer disparador',
    createdBy: 'Creado por',
    confirmDelete: {
      title: 'Confirmar Eliminación',
      message: '¿Estás seguro de que deseas eliminar este disparador? Esta acción no se puede deshacer.',
      triggerDetails: 'Detalles del Disparador',
      warning: 'Esta acción eliminará permanentemente el disparador y no se puede revertir.',
    },
    fields: {
      client: 'Cliente',
      agent: 'Agente',
      workflow: 'Workflow',
      step: 'Paso',
      status: 'Estado',
    },
    createSuccess: 'Disparador creado exitosamente',
    updateSuccess: 'Disparador actualizado exitosamente',
    deleteSuccess: 'Disparador eliminado exitosamente',
    saveError: 'Error al guardar el disparador',
    deleteError: 'Error al eliminar el disparador',
    activatedSuccess: 'Disparador activado',
    deactivatedSuccess: 'Disparador desactivado',
    toggleError: 'Error al cambiar el estado del disparador',
    validation: {
      workflowRequired: 'El workflow es obligatorio',
      stepsRequired: 'Selecciona al menos un paso',
      statusRequired: 'El estado es obligatorio',
    },
  },
  whatsapp: {
    chatLocation: {
      location: 'Ubicación',
    },
    title: 'Whatsapp',
    systemSender: 'Agencia',
    replyToThisMessage: 'Responder a este mensaje',
    linkedCases: 'Casos linkeados',
    noMessagesFound: 'No se encontraron mensajes..',
    typeMessage: 'Escribe tu mensaje...',
    startWithTemplate: 'Comienza con un template',
    messageStatus: {
      [WhatsappMessageEventStatusType.REQUESTED]: 'solicitado',
      [WhatsappMessageEventStatusType.ENQUEUED]: 'encolado',
      [WhatsappMessageEventStatusType.FAILED]: 'falló',
      [WhatsappMessageEventStatusType.SENT]: 'enviado',
      [WhatsappMessageEventStatusType.DELIVERED]: 'entregado',
      [WhatsappMessageEventStatusType.READ]: 'leído',
    },
    emoji: {
      categories: {
        favoritos: 'Favoritos',
        smileys_emotion: 'Caritas y emoción',
        people_body: 'Personas y cuerpo',
        travel_places: 'Viajes y lugares',
        food_drink: 'Comida y bebida',
        activities: 'Actividades',
        objects: 'Objetos',
        symbols: 'Símbolos',
        flags: 'Banderas',
      },
      names: {
        airplane: 'avión',
        globe: 'globo terráqueo',
        telephone: 'teléfono',
        speech_balloon: 'burbuja de diálogo',
        handshake: 'apretón de manos',
        smiling_face: 'cara sonriente con ojos alegres',
        face_with_hearts: 'cara con corazones',
        hugging_face: 'abrazo',
        star_struck: 'cara deslumbrada',
        folded_hands: 'manos en plegaria',
        thumbs_up: 'pulgar arriba',
        raising_hands: 'manos levantadas',
        briefcase: 'maletín',
        incoming_envelope: 'sobre entrante',
        rocket: 'cohete',
        check_mark: 'marca de verificación',
        alarm_clock: 'despertador',
        dollar_sign: 'signo de dólar',
        red_heart: 'corazón rojo',
        party_popper: 'confeti',
        grinning_face: 'cara feliz',
        beaming_face: 'cara sonriente',
        face_with_tears: 'cara llorando de risa',
        grinning_face_with_sweat: 'cara sonriente sudor frío',
        smiling_face_with_sunglasses: 'cara con gafas de sol',
        thinking_face: 'pensativo',
        face_with_rolling_eyes: 'ojos en blanco',
        crying_face: 'cara llorando',
        smiling_face_with_heart_eyes: 'cara enamorada',
        pouting_face: 'cara enfadada',
        sleeping_face: 'cara dormida',
        partying_face: 'cara de fiesta',
        waving_hand: 'saludo',
        thumbs_down: 'pulgar abajo',
        open_hands: 'manos abiertas',
        clapping_hands: 'aplausos',
        call_me_hand: 'gesto hang loose',
        flexed_biceps: 'brazo fuerte',
        car: 'coche',
        bus: 'autobús',
        train: 'tren',
        sailboat: 'velero',
        passenger_ship: 'crucero',
        statue_of_liberty: 'Estatua de la Libertad',
        world_map: 'mapa',
        eiffel_tower: 'Torre Eiffel',
        green_apple: 'manzana verde',
        hamburger: 'hamburguesa',
        pizza: 'pizza',
        sushi: 'sushi',
        steaming_bowl: 'ramen',
        shortcake: 'torta',
        beer_mug: 'jarra de cerveza',
        hot_beverage: 'café',
        wine_glass: 'copa de vino',
        bottle_with_popping_cork: 'botella de champagne',
        soccer_ball: 'fútbol',
        basketball: 'básquetbol',
        american_football: 'fútbol americano',
        tennis: 'tenis',
        volleyball: 'vóleibol',
        video_game: 'videojuego',
        musical_note: 'nota musical',
        clapper_board: 'claqueta',
        artist_palette: 'paleta de artista',
        mobile_phone: 'teléfono móvil',
        laptop: 'computadora portátil',
        desktop_computer: 'monitor',
        printer: 'impresora',
        camera: 'cámara',
        key: 'llave',
        locked: 'candado cerrado',
        wastebasket: 'papelera',
        package: 'caja',
        battery: 'batería',
        broken_heart: 'corazón roto',
        hundred_points: 'cien puntos',
        cross_mark: 'cruz',
        warning: 'advertencia',
        information: 'información',
        bell: 'campana',
        ok_button: 'OK',
        recycling_symbol: 'reciclaje',
        uruguay_flag: 'bandera de Uruguay',
        paraguay_flag: 'bandera de Paraguay',
        bolivia_flag: 'bandera de Bolivia',
        colombia_flag: 'bandera de Colombia',
        usa_flag: 'bandera de Estados Unidos',
        brazil_flag: 'bandera de Brasil',
        argentina_flag: 'bandera de Argentina',
        mexico_flag: 'bandera de México',
        chile_flag: 'bandera de Chile',
        spain_flag: 'bandera de España',
        star: 'estrella',
        glowing_star: 'estrella brillante',
        sparkles: 'destellos',
        dizzy: 'remolino de estrella',
        shooting_star: 'estrella fugaz',
      },
      selectEmoji: 'Seleccioná un emoji',
      searchPlaceholder: 'Escribí acá para buscar',
    },
    templateMessenger: {
      title: 'Enviar mensaje desde template',
      selectTemplate: 'Seleccioná un template',
      preview: 'Vista previa:',
      completeFields: 'Completa los campos:',
      placeholder: 'Placeholder {{number}}',
      sendMessage: 'Enviar mensaje',
      fileSelected: 'Archivo seleccionado',
      fileRequired: 'Se requiere un archivo para este template',
      uploadFile: 'Subir archivo',
      noFileSelected: 'Ningún archivo seleccionado',
      selectFile: 'Seleccionar archivo',
    },
    results: 'resultados',
    table: {
      clientName: 'Nombre del cliente',
      fromNumber: 'Número de teléfono',
      conversation: 'Conversación',
      agency: 'Agencia',
      msgType: 'Tipo de mensaje',
      expiration: 'Expiración',
    },
    client: {
      lastCases: 'Últimos Casos',
      noCases: 'No hay casos para mostrar',
      conversation: 'Conversación de Whatsapp',
      searchPlaceholder: 'Buscar en la conversación',
      writeMessage: 'Escribe un mensaje',
      sendTemplate: 'Enviar Template',
      aiEnabled: 'IA Habilitada',
      sentTo: 'envió a',
    },
    content: {
      whatsapp: 'Whatsapp',
      email: 'Correo electrónico',
      phone: 'Teléfono',
      files: 'Archivos',
      notes: 'Notas',
      history: 'Historial',
      all: 'Todos',
      show: 'Mostrar',
      search: 'Buscar...',
      viewMore: 'Ver más',
      now: 'Ahora',
      minutesAgo: 'Hace {{minutes}} minutos',
      sessionExpired: 'Sesión expirada',
      timeLeft: 'Tiempo restante',
    },
    messageTypes: {
      text: 'Texto',
      contacts: 'Contactos',
      template: 'Plantilla',
      sticker: 'Sticker',
      audio: 'Audio',
      video: 'Video',
      image: 'Imagen',
      document: 'Documento',
      location: 'Ubicación',
    },
    settings: {
      title: 'Configuración de Gupshup',
      createNewSubscription: 'Crear nueva suscripción',
      tag: 'Etiqueta',
      url: 'URL',
      version: 'Versión',
      showOnUI: 'Mostrar en UI',
      modes: 'Modos',
    },
    iaReasoning: {
      viewReasoning: 'Ver razonamiento de IA',
      hideDetails: 'Ocultar detalles',
      viewCompleteAnalysis: 'Ver análisis completo',
      contextAnalysis: 'Análisis del Contexto',
    },
  },
  agencyAccess: {
    title: 'Acceso a agencias',
    saveError: 'Ocurrio un problema al actualizar el permiso entre usuario y agencia',
  },
  updateClientAgency: {
    updateSuccess: 'Agencia actualizada!',
    updateError: 'Ocurrio un error al actualizar la agencia',
    title: 'Agencia Responsable',
    inputLabel: 'Seleccionar Agencia',
    confirm: 'Confirmar cambios',
    dialog1: 'Cambiar la agencia disparará varios cambios ',
    dialog2: 'El usuario dejar de formar parte de los teams perteneciente a esta agencia',
    dialog3: 'Todos los casos que pertenezcan a este usuario quedaran unassigned',
    dialog4: '¿Estás seguro que deseas continuar?',
  },
  agentAvailability: {
    title: 'Agentes Disponibles',
    status: 'Estado',
    agent: 'Agente',
    since: 'Hace',
  },
  agentStatus: {
    [AgentStatus.AFTER_CALL]: 'Seguimiento',
    [AgentStatus.AVAILABLE]: 'Disponible',
    [AgentStatus.BREAK]: 'Descanso',
    [AgentStatus.IN_CALL]: 'En llamada',
    [AgentStatus.LUNCH]: 'En Almuerzo',
    [AgentStatus.NOT_AVAILABLE]: 'No Disponible',
    [AgentStatus.NOT_APPLICABLE]: 'No Aplica',
    [AgentStatus.WORKING_IN_CASE]: 'Trabajando en caso',
    [AgentStatus.DISCONNECTED]: 'Desconectado',
  },
  roles: {
    title: 'Roles',
    [UserRoles.ADMINS]: 'Administrador',
    [UserRoles.AGENTS]: 'Agente',
    [UserRoles.OPERATORS]: 'Operador',
    [UserRoles.REGISTERED]: 'Registrado',
    [UserRoles.HOTEL_CONTENT_MODERATOR]: 'Moderador de contenido de hoteles',
    [UserRoles.EXTERNAL_INTEGRATION]: 'Integracion Externa',
    [UserRoles.TESTER]: 'Tester',
    [UserRoles.HOTEL_BOOKER]: 'Hotel Booker',
  },
  supportForms: {
    list: {
      title: 'Support forms',
      subtitle: 'Listado de support forms',
      results: 'resultados',
      error: 'Hubo un error intentando cargar los support forms',
      delete: 'Confirma para eliminar: {{name}}',
      deleteTitle: 'Eliminar support form',
      deleted: 'Support form eliminado!',
      deleteError: 'Hubo un problema al intentar eliminar el support form',
    },
    fields: {
      titleField: 'Titulo',
      workflowField: 'Workflow',
      descriptionField: 'Descripcion',
      customLogoUrl: 'URL de logo personalizado',
      customLogoUrlHelper: 'Se usará el logo de la agencia por defecto si no se completa',
      finalUrl: 'Url final',
      name: 'Nombre',
      lastName: 'Apellido',
      email: 'Correo electrónico',
      emailSubject: '{{title}} {{code}} 🚀',
    },
    view: {
      titleCreate: 'Crear Support Form',
      preview: 'Vista preliminar',
      noWorkflows: 'No se encontraron workflows',
      saved: 'Support form guardado!',
      error: 'Hubo un problema al intentar guardar el support form',
    },
    generator: {
      title: 'Agregar campos customizados',
      fieldType: 'Tipo de campo',
      optionsLabel: 'Opciones (separadas por coma)',
      label: 'Etiqueta',
      key: 'Name del field en el form',
      isRequired: 'Es requerido',
      addField: ' Agregar campo',
    },
    send: {
      success: 'Solicitud enviada correctamente',
      successMessage: 'Te contactaremos en breves!',
      error: 'Hubo un problema al intentar enviar la solicitud',
    },
    email: {
      title: 'Un nuevo caso fue abierto para: {{email}} con los siguientes datos procesados:',
    },
  },
  auth: {
    login: {
      title: 'Iniciá sesión en Me Vuelo',
      email: 'Correo electrónico',
      password: 'Contraseña',
      forgotPassword: '¿Olvidaste tu contraseña?',
      createAccountText: '¿No tenés cuenta?',
      createAccountLink: 'Registrate',
      loginMesssage: 'Iniciar sesión',
      logoutMesssage: 'Cerrar sesión',
      loginGoogleMesssage: 'Iniciar con Google',
      fieldErrorMessage: 'Este campo es requerido',
      error: 'Usuario o password invalido',
    },
    forgotPassword: {
      title: '¿Olvidaste tu contraseña?',
      subtitle: 'No te preocupes, te enviaremos un código para restablecerla',
      email: 'Ingresa tu correo',
      reset: 'Restablecer contraseña',
      back: 'Volver',
      error: 'Hubo un problema',
      sentEmail: 'Correo enviado',
    },
    resetPasswordCode: {
      title: 'Restablecimiento de contraseña',
      subtitle: 'Te enviamos un código a',
      continue: 'Continuar',
      back: 'Volver',
      error: 'Hubo un problema',
    },
    resetPassword: {
      title: 'Establece una nueva contraseña',
      newPassword: 'Nueva contraseña',
      confirmNewPassword: 'Confirmar contraseña',
      save: 'Restablecer contraseña',
      error: 'Hubo un problema',
      success: 'Contraseña restablecida con exito',
    },
    register: {
      title: 'Registrate a Me Vuelo',
      email: 'Correo electrónico',
      password: 'Contraseña',
      confirm: 'Confirma tu contraseña',
      registerMesssage: 'Registrarse',
      logInText: '¿Ya tenés cuenta?',
      logInLink: 'Iniciá sesión',
      fieldErrorMessage: 'Este campo es requerido',
      userRegistered: 'Usuario registrado: ahora iniciá sesión',
      error: 'El correo electrónico ya existe en nuestro systema',
      errorMessage: 'Usuario o password invalido!',
    },
    changePassword: {
      title: 'Cambiar contraseña',
      currentPassword: 'Contraseña actual',
      newPassword: 'Nueva contraseña',
      confirmNewPassword: 'Confirmar contraseña',
      passwordsDontMatch: 'Las contraseñas no coinciden',
      changePasswordError: 'Ha ocurrido un error al intentar cambiar la contraseña',
      success: 'Contraseña cambiada correctamente',
      invalidPassword: 'Contraseña incorrecta',
    },
    editUser: {
      title: 'Editar datos personales',
      subtitle: 'Ingresa los datos como aparecen en tu documento de identificación o pasaporte.',
      failMessage: 'Error al actualizar el usuario',
      uploadPicture: 'Subir imagen',
      pictureFieldDescription: 'Añade una foto. Tamaño máximo del archivo 3MB',
      firstName: {
        placeHolder: 'Nombres',
        errorMessage: 'Campo requerido',
        helperText: 'Tal cual figura en su DNI',
      },
      lastName: {
        placeHolder: 'Apellidos',
        errorMessage: 'Campo requerido',
        helperText: 'Tal cual figura en su DNI',
      },
      phoneCountryCode: {
        placeHolder: 'Codigo de area',
        errorMessage: 'Campo requerido',
      },
      phoneNumber: {
        placeHolder: 'Numero de telefono',
        errorMessage: 'Campo requerido',
      },
      phoneExtension: {
        placeHolder: 'Numero de extensión',
        errorMessage: 'Este campo falló',
        helperText: 'Numero de extensión (opcional)',
      },
      position: {
        placeHolder: 'Posición',
        errorMessage: 'Este campo falló',
      },
      address1: {
        placeHolder: 'Direccion',
        errorMessage: 'Campo requerido',
        helperText: 'Ingrese su direccion',
      },
      address2: {
        placeHolder: '',
        errorMessage: 'Campo requerido',
        helperText: '2nda linea de direccion (opcional)',
      },
      country: {
        placeHolder: 'Pais',
        errorMessage: 'Campo requerido',
      },
      city: {
        placeHolder: 'Ciudad',
        errorMessage: 'Campo requerido',
      },
      postalCode: {
        placeHolder: 'Codigo Postal',
        errorMessage: 'Campo requerido',
      },
      documentType: {
        placeHolder: 'Tipo de documento',
        errorMessage: 'Campo requerido',
        helperText: 'Tipo de documento',
      },
      documentNumber: {
        placeHolder: 'Numero de documento',
        errorMessage: 'Campo requerido',
        helperText: 'Tal cual figura en el documento',
      },
      profilePicture: {
        errorMessage: 'Hubo un problema subiendo tu foto.',
        fileTooLarge: 'El archivo es demasiado grande, el tamaño máximo es de 3MB',
        invalidFileFormat: 'Formato de archivo no soportado.',
      },
      applyChangesBtn: 'Aplicar',
      patchSuccess: 'Usuario actualizado con éxito!',
    },
  },
  noteEditor: {
    subject: 'Asunto',
    save: 'Guardar',
    send: 'Enviar',
    to: 'Para',
    cc: 'CC',
    bcc: 'BCC',
    from: 'De',
    placeholder: 'Escribe algo',
    emailTitle: 'Nuevo E-mail',
    noteTitle: 'Nueva nota',
    noOptions: 'No se encontraron resultados',
    controls: {
      minimize: 'Minimizar',
      maximize: 'Maximizar',
      close: 'Cerrar',
      remove: 'Remover',
      bold: 'Negrita',
      italic: 'Itálica',
      underline: 'Subrayado',
      bulletList: 'Lista',
      attachments: 'Adjustar',
      link: 'Link',
      image: 'Imagen',
      undo: 'Deshacer',
      redo: 'Rehacer',
    },
    errors: {
      subject: 'El asunto es requerido',
      content: 'El contenido es requerido',
      email: 'El destinatario es requerido',
      fileTooLarge: 'El archivo es demasiado grande, el tamaño máximo es de 3MB',
      fileNotAvailable: 'El archivo no está disponible',
    },
  },
  calls: {
    subtitle: 'Lista de llamadas del sistema',
    table: {
      title: 'Llamadas',
      type: 'Tipo',
      from: 'Desde',
      to: 'Hacia',
      agent: 'Agente',
      status: 'Estado',
      duration: 'Duración',
      inbound: 'Entrante',
      outbound: 'Saliente',
      notAssigned: 'Sin asignar',
    },
    status: {
      [CallStatus.IN_PROGRESS]: 'En curso...',
      [CallStatus.DECLINED]: 'No contestada',
      loading: 'Cargando...',
      unknown: 'Desconocido',
    },
    assignment: {
      title: 'Detalle de la llamada',
      search: 'Buscar caso',
      newCase: 'Nuevo caso',
      caseId: 'ID del caso',
      success: 'Llamada asignada con éxito',
      error: 'Hubo un problema al asignar la llamada',
      flow: 'Flujo',
      client: 'Cliente',
      clientUnassigned: 'Sin asignar',
      step: 'Paso',
      status: 'Estado',
      team: 'Equipo',
      assignedTo: 'Agente asignado',
      link: 'Asociar a este caso',
      create: {
        title: 'Nuevo caso',
        cancel: 'Cancelar',
        accept: 'Crear',
        subject: 'Asunto del caso',
        unassigned: 'Sin asignar',
        warning: 'La llamada en curso se asociará a este nuevo caso',
      },
      filters: {
        title: 'Filtrar por',
        team: 'Equipo',
        workflow: 'Flujo',
        step: 'Paso',
        agent: 'Agente',
        client: 'Cliente',
      },
    },
    incoming: {
      title: 'Llamada entrante',
      callFrom: 'Llamada de {{phoneNumber}}',
      unknown: 'Desconocido',
      contentSubtitle: 'Te está llamando',
      answer: 'Aceptar',
      phone: 'Teléfono',
      decline: 'Colgar',
      transfer: {
        title: 'Transferir llamada',
        searchPlaceholder: 'Buscar usuario',
        freqUsers: 'Usuarios frecuentes',
        calling: 'Llamando...',
        joined: 'En curso',
      },
    },
    interaction: {
      title: 'Habilitar recepción de llamadas',
      description:
        'Para recibir llamadas y reproducir sonidos automáticamente, necesitas hacer clic en cualquier parte de la pantalla.',
      enable: 'Habilitar llamadas',
      enable_audio: 'Habilitar sonido',
      banner_message:
        'Haz clic aquí para habilitar el sonido de llamadas entrantes. También puedes hacer clic en cualquier parte de la página.',
    },
    inProgress: { title: 'Llamada en curso', caseNumber: 'Caso #{{caseId}}' },
  },
  customFields: {
    title: 'Campos Personalizados',
    subtitle: 'Lista de campos personalizados',
    fieldBlockCreate: {
      title: 'Crear bloque de campos',
      name: 'Nombre',
      createdSuccess: 'Bloque de campos creado',
      createdError: 'Ocurrio un error al crear el bloque de campos',
    },
    fieldBlockEdit: {
      title: 'Editar bloque de campos',
      editSuccess: 'Bloque de campos actualizado',
      editError: 'Ocurrio un error al editar el bloque de campos',
    },
  },
  common: {
    all: 'Todas',
    hours: 'horas',
    view: 'Ver',
    create: 'Crear',
    delete: 'Eliminar',
    deleting: 'Eliminando...',
    active: 'Activo',
    inactive: 'Inactivo',
    mode: 'Modo',
    count: 'Cantidad',
    name: 'Nombre',
    noData: 'Sin datos disponibles',
    comingSoon: 'Próximamente',
    mandatoryField: 'Este campo es obligatorio',
    invalidDate: 'Fecha inválida',
    add: 'Agregar',
    close: 'Cerrar',
    update: 'Actualizar',
    continue: 'Continuar',
    cheapest: 'Más económicos',
    nonEconomic: 'Más caros',
    recommended: 'Recomendados',
    orderBy: 'Ordernar por:',
    na: 'N/A',
    accept: 'Aceptar',
    confirmAction: 'Confirmar Acción',
    goBack: 'Volver atras',
    warning: 'Cuidado',
    save: 'Guardar',
    today: 'Hoy',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    unassigned: 'Sin asignar',
    yes: 'Si',
    no: 'No',
    welcomeMessage: 'Hola mundo! este es un mensaje en español de bienvenida para: ',
    filters: 'Filtros',
    general: 'General',
    notValueFound: '-',
    results: 'Resultados',
    search: 'Buscar',
    refresh: 'Refrescar',
    refreshData: 'Refrescar datos',
    status: 'Estado',
    step: 'Paso',
    workflow: 'Flujo',
    channel: 'Canal',
    agent: 'Agente',
    updated: 'Actualizado',
    list: 'Lista',
    new: 'Nuevo',
    takeALook: 'Ver',
    selectFile: 'Seleccionar archivo',
    tbd: 'TBD',
    maxSelectionsReached: 'Máximo de selecciones alcanzado',
    itemsSelected: 'items seleccionados',
    readMore: 'Ver más',
    showLess: 'Ver menos',
    actions: {
      add: 'Agregar',
      close: 'Cerrar',
      cancel: 'Cancelar',
      create: 'Crear',
      save: 'Guardar',
      gptGeneration: 'Generar con GPT',
      gptAllGeneration: 'Generar todo con GPT',
      open: 'Abrir',
      remove: 'Borrar',
      send: 'Enviar',
      download: 'Descargar',
      edit: 'Editar',
    },
    form: {
      onlyLetters: 'Solo letras',
      onlyNumbers: 'Solo números',
      onlyLettersAndNumbers: 'Solo letras y números',
      pressEnterToAdd: 'Presiona enter para agregar',
      invalidMail: 'Correo inválido',
      duplicateMail: 'Correo duplicado',
      apply: 'Aplicar',
      clear: 'Limpar',
      required: 'Campo requerido',
      save: 'Guardar',
      discard: 'Descartar',
      select: 'Seleccionar',
      delete: 'Eliminar',
      url: 'El campo es una URL',
    },
    nav: {
      flights: 'Vuelos',
      hotels: 'Hoteles',
      cars: 'Autos',
      explorer: 'Explorar',
      dashboard: 'Dashboard',
      saleOrders: 'Sale Orders',
      clients: 'Clientes',
      agencies: 'Agencias',
      cases: 'Casos',
      calls: 'Llamadas',
      teams: 'Equipos',
      workflows: 'Flujos de trabajo',
      whatsapp: 'Whatsapp',
      users: 'Usuarios',
      flightIntegrations: 'Integraciones de vuelo',
      hotelIntegrations: 'Integraciones de hotel',
      hotelContentManagement: 'Hotel Content',
      userMenu: {
        userIdentifier: 'Invitado',
        myFavorites: 'Mis favoritos',
        myNotifications: 'Notificaciones',
        myBookings: 'Mis Reservas',
        admin: 'Admin',
        routingRules: 'Routing rules',
        markupRules: 'Reglas de margenes',
        userList: 'Listado de usuarios',
        contentManager: 'Gestor de Contenidos',
        accountMessage: 'Cuenta',
        currentCurrency: 'Moneda',
        currentLanguage: 'Idioma',
        help: 'Ayuda',
        selectedLanguage: 'Español',
        teams: 'Teams',
      },
    },
    footer: {
      companyType: 'Agencia de viajes registrada',
      identity: 'Real Consulting S.A. Resolución Registur N° 1263/2019',
      contactMessage: 'Contactanos',
      company: {
        title: 'Empresa',
        about: 'Sobre Nosotros',
        terms: 'Términos y Condiciones',
        privacy: 'Políticas de Privacidad',
        join: 'Sumate al Equipo',
      },
      account: {
        title: 'Cuenta',
        data: 'Mis Datos',
        favourites: 'Mis Favoritos',
        alerts: 'Mis Alertas',
        reserves: 'Mis Reservas',
        close: 'Cerrar Sesión',
      },
      platform: {
        title: 'Plataforma',
        currency: 'Moneda',
        language: 'Idioma',
        rates: 'Rates Tracker',
        rules: 'Markup Rules',
        travelers: 'Mis Viajeros',
        airlines: 'ABM Aerolineas',
        cabines: 'ABM Cabinas',
        alliances: 'ABM Alianzas',
      },
      payments: {
        certification: {
          title: 'Certificación de seguridad',
          description:
            'Mevuelo.com se encuentra certificado por el consejo de normas de seguridad para tus medios de pago.',
        },
        intelligentTravel: {
          title: 'Viajá inteligente y gastá menos',
          description:
            'Descubrí precios inmejorables en vuelos, hoteles y autos, haciendo de cada viaje una aventura económica.',
        },
        explore: {
          title: 'Explorá más, preocupate menos',
          description:
            'Con Mevuelo, viajar es más fácil que nunca. Personalizá tu viaje como quieras, empacá tus sueños y nosotros nos encargamos del resto.',
        },
        paymentMethods: {
          title: 'Pagá con el método que más te guste',
          credit: 'Tarjetas de crédito',
          bankTransfers: 'Transferencias bancarias',
          cash: 'Efectivo en cualquiera de nuestras sucursales',
        },
        altImages: {
          visa: 'logo de la tarjeta visa',
          oca: 'logo de la tarjeta oca',
          mastercard: 'logo de la tarjeta mastercard',
          american: 'logo de la tarjeta american',
          securityEncryption: 'imagen caja de seguridad',
          wallet: 'imagen billetera',
          backpack: 'imagen mochila',
          card: 'icono de tarjeta',
          bank: 'icono de banco',
          cash: 'icono de dinero',
        },
      },
      help: {
        title: 'Ayuda',
        faq: "FAQ's",
      },
    },
    priceTrend: {
      message: 'Tendencia de precios',
    },
    tableList: {
      numberOfResults: '{{count}} Resultados',
      rowsPerPage: 'Filas por página:',
      empty: 'Opss.. No se encontraron resultados',
    },
    fields: {
      country: 'País',
      provider: 'Provedor',
      status: 'Status',
      actions: 'Acciones',
      uploadFile: 'Subir archivo',
    },
    language: {
      english: 'Inglés',
      portuguese: 'Portugués',
      spanish: 'Español',
    },
    error: {
      server: 'Ops! Algo salio mal! :(',
      client: 'Ops! La acción solicitada no pudo ejecutarse',
      unathorized: 'No se puede verificar tus credenciales',
      documentNotSupported: 'El tipo de archivo no es soportado',
    },
  },
  dashboard: {
    title: 'Tablero',
    subtitle: 'Descripcion general',
    casesByChannel: 'Casos por analisis de ingreso',
    casesByDay: 'Casos creados por día',
    casesByHourAndChannel: 'Casos creados por hora y analisis de ingreso',
    casesNeedAgentByTime: 'Casos necesitando agente por tiempo',
    missingAgent: 'Necesitan agente',
    todayCases: 'Casos creados hoy',
    connectedAgents: 'Agentes conectados',
    sections: {
      importantNumbers: 'Números Importantes',
      needsAgent: 'Necesita Agente',
      needAgentByTeam: 'Casos que necesitan agente por equipo',
      needAgentByChannel: 'Casos que necesitan agente por canal',
      noDataAvailable: 'No hay datos disponibles',
      quickComparisons: 'Comparativas Rápidas',
      agentsStatus: 'Estado de Agentes',
      activityTrends: 'Tendencias de Actividad',
    },
    kpis: {
      availableAgents: 'Agentes disponibles',
      needAgentCases: 'Casos necesitan agente',
      callsOnHold: 'Llamadas en espera',
      avgEmailResponse: 'Promedio respuesta Email',
      avgWhatsappResponse: 'Promedio respuesta WhatsApp',
      noData: '-',
    },
    charts: {
      unassignedCasesByTeam: 'Casos sin asignar por equipo',
      avgResponseByChannel: 'Promedio de respuesta por canal (minutos)',
      casesByHourPattern: 'Promedio de casos abiertos por hora los {{day}}',
      casesByWeekday: 'Promedio de casos abiertos por día de semana',
    },
    comparatives: {
      casesResolved: 'Casos Resueltos',
      casesCreated: 'Casos Creados',
      whatsappConversations: 'Conversaciones WhatsApp',
      incomingCalls: 'Llamadas recibidas',
      todayVsYesterday: 'Hoy vs Ayer',
      thisWeekVsPrevious: 'Esta semana vs anterior',
      vs: 'vs',
      casesNeedingAgent: 'Casos que necesitan agente por tiempo',
      timeRanges: {
        '0-1': '1 hora o menos',
        '1-3': '1-3 horas',
        '3-8': '3-8 horas',
        '8-24': '8-24 horas',
        '24-48': '24-48 horas',
        '+48': 'Más de 48 horas',
      },
    },
    agentsStatus: {
      agentStates: 'Estados de Agentes',
      agentsList: 'Lista de Agentes',
      totalAgents: 'Total Agentes',
      updatedAgo: 'Actualizado hace {{time}}',
      noAgentsConnected: 'No hay agentes conectados',
    },
    trends: {
      weekday: 'Día de la semana',
      startDate: 'Fecha inicio',
      endDate: 'Fecha fin',
      weekdays: {
        monday: 'Lunes',
        tuesday: 'Martes',
        wednesday: 'Miércoles',
        thursday: 'Jueves',
        friday: 'Viernes',
        saturday: 'Sábado',
        sunday: 'Domingo',
      },
      weekdaysShort: {
        mon: 'Lun',
        tue: 'Mar',
        wed: 'Mié',
        thu: 'Jue',
        fri: 'Vie',
        sat: 'Sáb',
        sun: 'Dom',
      },
    },
    filters: {
      agencies: 'Agencias',
      teams: 'Equipos',
      channels: 'Canales',
      agency: 'Seleccionar Agencia',
      agencyPlaceholder: 'Todas las agencias',
      startDate: 'Fecha Desde',
      endDate: 'Fecha Hasta',
      withoutTeam: 'Sin equipo',
    },
    channels: {
      whatsapp: 'Whatsapp',
      call: 'Llamada',
      email: 'Email',
      externalIntegration: 'Integración Externa',
      manual: 'Manual',
      uploaded: 'Subido',
      supportForm: 'Formulario de Soporte',
    },
  },
  bookings: {
    view: {
      title: 'Booking',
      subtitle: 'Informacion de la reserva',
      bookingNumber: 'Reserva #',
      cancelBooking: 'Cancelar',
      issueBooking: 'Emitir',
      caseNumber: 'Caso #',
      quotationNumber: 'Cotizacion #',
      salesOrderNumber: 'Salesorder #',
      created: 'Fecha de creacion',
      passengers: 'Pasajeros',
      name: 'NOMBRES',
      lastname: 'APELLIDOS',
      document: 'DOCUMENTO',
      paxType: 'TIPO DE PAX',
      nightsIn: 'noches en',
      finalPrice: 'Valor final total',
    },
    title: 'Bookings',
    subtitle: 'Listado de bookings',
    table: {
      holder: 'Titular',
      paxQty: 'Pasajeros',
      destination: 'Destino',
      fareProvider: 'Proveedor de tarifa',
      serviceProvider: 'Proveedor de servicio',
      status: 'Estado',
      reservationCode: 'Reserva',
    },
    status: {
      open: 'abierto',
      pending: 'pendiente',
      failed: 'fallo',
      confirmed: 'confirmada',
      completed: 'completada',
      cancelled: 'cancelada',
      not_found: 'no encontrada',
      expired: 'expirada',
      unknown: 'desconocido',
    },
  },
  flights: {
    searcher: {
      ariaLabelSearcher: 'Motor de vuelos',
      oneWay: 'Solo ida',
      roundTrip: 'Ida y vuelta',
      multidestination: 'Multidestino',
      origin: 'Origen',
      destination: 'Destino',
      departureDate: 'Fecha de partida',
      arrivalDate: 'Fecha de retorno',
      go: 'Ida',
      return: 'Vuelta',
      unknownDates: 'Todavía no decido las fechas',
      economic: 'Economica',
      guest: 'Pasajero',
      search: 'Buscar',
      addSection: 'Añadir segmento',
      section: 'Tramo {{amount}}',
    },
    availabilityLayout: {
      editSearchTitle: 'Editar Busqueda',
      avatarAriaLabel: 'Icono Abatar de cama de Hotel',
      viewMapAriaLabel: 'boton abrir vista de mapa',
      tuneFiltersAriaLabel: 'Boton Abrir Filtros',
      result_one: 'resultado',
      result_other: 'resultados',
      viewMap: 'Ver mapa',
      loadingFlightsLegend: 'Estamos buscando las mejores ofertas de vuelo para vos.',
      loadingMoreFlights: 'Cargando más vuelos...',
      flightDetail: 'Detalle de vuelo',
      isReturn: 'vuelta',
      isStart: 'ida',
      totalDuration: 'Duración total',
      noFareDataAvailable: 'No hay data disponible de esta tarifa',
      noFares: 'No hay tarifas disponibles que concuerden con tu busqueda',
    },
    cardPackage: {
      direct: 'Directo',
      doubleBaseText: 'Valor por persona en dobles',
      fromText: 'Desde',
      reserveButtonText: 'Reservar',
      night_one: 'noche',
      night_other: 'noches',
      stop_one: 'Escala',
      stop_other: 'Escalas',
    },
    availabilityCard: {
      recommendedLabel: 'Recomendado',
    },
    booking: {
      purchaseDetails: 'Detalle de compra',
      total: 'total',
      continueBooking: 'continuar',
    },
    payment: {
      summary: 'Resumen de compra',
      total: 'Pago total',
      success: '¡Pago exitoso!',
      failed: 'Pago no exitoso',
      method: 'Medio de pago',
      yours: 'Tu pago',
      serviceFare: 'Tarifa de servicio',
      taxes: 'Impuestos',
      date: 'Fecha de pago',
    },
    voucher: {
      card: 'Tarjeta de embarque',
      localizer: 'Localizador de reserva',
      agency: 'Agencia',
      sits: 'Asientos',
      gohome: 'volver al home',
      gate: 'Puerta',
      terminal: 'Terminal',
      arrival: 'Llegada',
      departure: 'Salida',
    },
  },
  hotels: {
    farebreakDownModal: {
      title: 'Desgloce de la tarifa',
      netAmount: 'Importe neto',
      grossAmount: 'Importe bruto',
      included: 'incluido',
      excluded: 'a pagar en hotel',
      markup: 'Markup',
      fixed: 'fijo',
      total: 'Total',
    },
    rating: {
      opinion_one: '({{formattedCount}} opinión)',
      opinion_other: '({{formattedCount}} opiniones)',
      error: 'Se produjo un error al obtener la calificación del hotel. Contactá con el administrador del sistema.',
    },
    cancelPolicyModal: {
      title: 'Política de Cancelación',
      refundableFare: 'Tarifa reembolsable',
      penaltyNights: 'noche(s) de penalización',
      fromTotal: '% del total',
      fixedAmount: 'Importe fijo',
      unknownPenalty: 'Penalidad desconocida',
      limitDate: 'Aplica desde',
      penaltyTitle: 'Penalización por cancelación',
      additionalDetails: 'Detalles adicionales',
      dateCaluclatedByProvider: 'Origen de la fecha calculada por el proveedor',
      dateCaluclatedBySystem: 'Origen de la fecha calculada por el sistema',
    },
    availability: {
      fareCount: 'Hasta {{count}} opciones',
      noMarkup: 'Precio sin markup',
      loading: 'Cargando resultados..',
      empty: 'Uups, no se encontraron resultados. Por favor intenta de nuevo en algunos minutos.',
      error: 'Uups, algo salió mal. Por favor intenta de nuevo en algunos minutos.',
    },
    fares: {
      filters: {
        searchByName: 'Buscar por nombre',
        board: 'Board',
        provider: 'Proveedor',
        onlyRefundable: 'Solo reembolsables',
      },
      amenitites: 'Comodidades',
      showAllAmenitiesTrue: 'Mostrar todas las comodidades',
      showAllAmenitiesFalse: 'Ocultar todas las comodidades',
      showCompleteDescriptionTrue: 'Leer más',
      showCompleteDescriptionFalse: 'Ocultar despcripción',
      about: 'Acerca de este hotel',
      rooms: 'Selecciona tu opción',
      summary: 'Resumen de tu reserva',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      checkMinAge: 'Edad mínima para el check-in/check-out',
      checkInstructions: 'Instrucciones para check-in/check-out',
      remarks: 'Observaciones',
      paymentType: {
        MERCHANT: 'El pago es gestionado por el proveedor',
        DIRECT:
          'El pago se realiza directamente al beneficiario final (por ejemplo, el hotel), sin involucrar a un intermediario o tercero. El pago se completará al momento del check-in',
        CARD_BOOKING: 'El pago es gestionado por el proveedor. El pago se efectúa en el momento de la reserva',
        CARD_CHECK_IN: 'El pago es gestionado por el proveedor y se realiza en el hotel durante el check-in',
      },
      paxes: {
        title: 'Habitación para:',
        adult: 'Un adulto ({{age}} años)',
        children: 'Un niño ({{age}} años)',
      },
      room: {
        title: 'Habitación: ',
      },
      detail: {
        price: 'Precio: ',
        refundable: 'Reembolsable: ',
        paymentType: 'Método de pago: ',
      },
      hotelPaxData: {
        pax: {
          addInfo: 'Agregar información de pasajeros',
          useExisting: 'Usar pasajero existente',
        },
        name: {
          title: 'Nombre',
          required: 'El nombre es requerido.',
        },
        surname: {
          title: 'Apellido',
          required: 'El apellido es requerido.',
        },
        lastName: {
          title: 'Apellido',
          required: 'El apellido es requerido.',
        },
        birthDate: {
          title: 'Fecha de Nacimiento',
          required: 'La fecha de nacimiento es requerida.',
          notValid: 'Por favor ingrese una fecha de nacimiento válida.',
          notPast: 'La fecha de nacimiento no puede ser en el futuro.',
        },
        email: {
          title: 'Email',
          notValid: 'Por favor ingrese un email válido.',
        },
        phone: {
          title: 'Teléfono',
        },
        form: {
          age_one: '{{count}} año',
          age_other: '{{count}} años',
          index: 'Pasajero: {{count}}',
        },
      },
      cancelPolicy: {
        title: 'Políticas de cancelación',
        details: 'Ver detalles',
        deadline: 'Cancelación gratuita hasta',
        refundable: 'Reembolsable',
        nonRefundable: 'No reembolsable',
      },
      rateRules: {
        title: 'Reglas de tarifa:',
        values: {
          NORMAL: 'Normal',
          PACKAGE: 'Paquete',
          OLDER55: 'Mayores de 55 años',
          OLDER60: 'Mayores de 60 años',
          OLDER65: 'Mayores de 65 años',
          CANARY_RESIDENT: 'Residente canario',
          BALEARIC_RESIDENT: 'Residente balear',
          LARGE_FAMILY: 'Familia numerosa',
          HONEYMOON: 'Luna de miel',
          PUBLIC_SERVANT: 'Funcionario público',
          UNEMPLOYED: 'Desempleado',
          NON_REFUNDABLE: 'No reembolsable',
          MOBILE: 'Móvil',
          WARRANTY: 'Garantía',
          ESSENTIAL_WORKER: 'Trabajador esencial',
          NEGOTIATED: 'Negociado',
        },
      },
    },
    searcher: {
      title: 'Búsqueda de hoteles',
      destination: 'Destino',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      unknownDates: 'Todavía no decido las fechas',
      room: 'Habitaciones',
      guest: 'Huéspedes',
      search: 'Buscar',
      apply: 'Aplicar',
      hotels: 'Hoteles',
      locations: 'locations',
    },
    roomMenu: {
      roomAmount: 'Cantidad',
      room: 'Habitación',
      year: 'año',
      years: 'años',
      adults: 'Adultos',
      minors: 'Menores',
      apply: 'Aplicar',
      selected: '{{count}} habitación seleccionada',
      selected_other: '{{count}} habitaciones seleccionadas',
      minorAge: 'Edad del menor: {{index}}',
      adultAge: 'Edad del Adulto: {{index}}',
      yearsOld: '{{count}} año',
      yearsOld_other: '{{count}} años',
      lessThanOneYear: 'Menor de un año',
      error: 'Por favor revisá que todos los campos este correctamente completeados',
    },
    cardsDestination: {
      seeMore: 'Ver más',
      from: 'Desde',
    },
    cardPackage: {
      doubleBaseText: 'Valor por persona en base doble',
      fromText: 'Desde',
      reserveButtonText: 'Reservar',
      hotelRating: 'Hotel {{stars}} estrellas',
      nightsOfDuration: '{{amount}} noches',
    },
    filter: {
      filters: 'Filtros',
      orderByAriaLabel: 'Grupo de filtros para ordenar por',
      orderBy: 'Ordenar por',
      recommended: 'Recomendados',
      bestPrice: 'Mejor precio',
      moreReviews: 'Más opiniones',
      score: 'Puntuación',
      name: 'Nombre',
      searchDistanceAriaLabel: 'Deslizador de distancia de búsqueda',
      searchDistance: 'Distancia de búsqueda',
      radiusMessage: 'En un radio de',
      popularFiltersAriaLabel: 'Popular filters group',
      popularFilters: 'Grupo de filtros populares',
      allInclusive: 'All inclusive',
      oceanView: 'Ocean view',
      hotelResort: 'Hotel resort',
      hotel: 'Hotel',
      freeCancel: 'Cancelación gratis',
      noPaymentInAdvance: 'Sin pago por adelantado',
      priceAriaLabel: 'Slider de rango de precios',
      price: 'Precio',
      travelersExperienceAriaLabel: 'Grupo de filtros de experiencia de viajeros',
      travelersExperience: 'Experiencia de viajeros',
      lgbtq: 'LGBTQ',
      businessFriendly: 'Business friendly',
      reducedMobility: 'Movilidad reducida',
      onlyAdults: 'Only adults',
    },
    availabilityLayout: {
      editSearchTitle: 'Editar búsqueda',
      avatarAriaLabel: 'Icono ávatar de cama de hotel',
      viewMapAriaLabel: 'Botón para ver mapa',
      tuneFiltersAriaLabel: 'Botón para ajustar filtros',
      results: 'resultados',
      viewMap: 'Ver mapa',
    },
    availabilityCard: {
      opinions: '{{numberOfOpinions}} opiniones',
      endValueText: 'Valor final total desde',
      hotelRating: 'Hotel {{rating}} estrellas',
      allInclusiveLabel: 'All inclusive',
      recommendedLabel: 'Recomendado',
    },
    markupRules: {
      buttonNewRule: '+ NUEVA REGLA',
      buttonBackHome: 'Volver al home',
      shownElements: 'Elementos mostrados',
      listTitle: 'Listado de reglas',
      search: 'Buscador',
    },
    routingRules: {
      buttonNewRule: '+ NUEVA REGLA',
      buttonBackHome: 'Volver al home',
      shownElements: 'Elementos mostrados',
      listTitle: 'Listado de reglas',
      search: 'Buscador',
    },
    propertyMapDrawer: {
      hotelDetails: 'Detalles del hotel',
      value: 'Valor final total desde',
      reserveButton: 'Reservar',
    },
    propertyMapCard: {
      details: 'Detalles',
      opinions: 'Opiniones',
      reserveButton: 'Reservar',
      popularAmenities: 'Comodidades populares',
    },
    contentManager: {
      title: 'Contenido',
      subtitle: 'Listado de hoteles',
      search: 'Buscar hoteles..',
      error: 'Ha ocurrido un error obteniendo los hoteles.',
      status: {
        managed: 'Revisado',
        outdated: 'Desactualizado',
        toReview: 'Por Revisar',
      },
      fields: {
        id: 'Hotel ID',
        idSub: 'Internal ID: {{id}}',
        code: 'TGX ID',
        accessCode: 'Proveedor',
        name: 'Nombre',
        country: 'País',
        city: 'Ciudad',
        address: 'Dirección',
        impressions: '# Impresiones',
        status: 'Status',
        linked: 'Asociado',
        modParent: 'ID Padre',
        modParentName: 'Nombre del Padre',
        priority: 'Prioridad',
      },
      filters: {
        status: {
          managed: 'Administrado',
          outdated: 'Desactualizado',
          toReview: 'Revisar',
        },
      },
      configuration: {
        title: 'Configuración',
        tabs: {
          providers: {
            title: 'Proveedores',
          },
          imageTags: {
            title: 'Tags para imagenes',
          },
          updates: {
            title: 'Actualizacion',
          },
          amenities: {
            title: 'Amenities',
            subTitle: 'Listado de iconos de amenities disponibles',
            translationTo: 'Traducido al local actual',
          },
          amenitiesMapping: {
            title: 'Mapeo de Amenities',
            subTitle: 'Selecciona un proveedor y mapea los amenitites',
            notMapped: 'No hay amenities mapeadas todavía',
            mapped: 'Amenity mapeada correctamente',
          },
          categoriesMapping: {
            title: 'Mapeo de Categorías',
            subTitle: 'Selecciona un proveedor y mapea las categorías',
            notMapped: 'No hay categorías mapeadas todavía',
            mapped: 'Categoría mapeada correctamente',
          },
        },
        defaultValues: {
          description: 'Descripcion predeterminada',
          location: 'Ubicación predeterminada',
          rooms: 'Habitaciones predeterminadas',
          images: 'Imágenes predeterminadas',
          amenities: 'Amenities predeterminadas',
          updateSuccess: 'Valor predeterminado actualizado correctamente',
          updateError: 'Ha ocurrido un error actualizando el valor predeterminado',
          getError: 'Ha ocurrido un error obteniendo los valores predeterminados',
        },
      },
      detail: {
        title: 'Resumen de hotel',
        linkedHotels: {
          title: 'Hoteles asociados',
          manually: 'Link manually',
        },
        error: {
          detail: 'Ha ocurrido un error obteniendo el hotel seleccionado. Contactacte al administrador del sitio',
          detailInfo:
            'Ha ocurrido un error obteniendo información del hotel seleccionado. Contactacte al administrador del sitio',
        },
        description: {
          tab: 'Hotel',
          title: 'Mi descripción',
          editor: {
            name: {
              required: 'El nombre del hotel es obligatorio',
            },
            rating: {
              required: 'La calificación por estrellas es obligatoria',
            },
            descriptions: {
              required: 'La descripción es obligatoria',
              card: 'Descripción en la card',
              detail: 'Descripción del detalle',
            },
            generateByAI: 'Do you want to generate descriptions by AI?',
          },
          update: {
            success: 'Descripciones actualizadas correctamente',
            error: 'Ha ocurrido un error actualizando las descripciones. Contacte al administrador del sitio',
          },
        },
        checkOptions: {
          tab: 'Check In / Out',
          title: 'Check In / Check Out',
          editor: {
            addOptions: 'Agregar',
            type: {
              title: 'Tipo',
              checkIn: 'Check In',
              checkOut: 'Check Out',
            },
            minAge: {
              title: 'Edad mínima',
              required: 'La edad mínima es requerida.',
              error: 'Edad inválida.',
              value: '{{value}} años',
            },
            startTime: {
              title: 'Desde',
              required: 'La hora "desde" es requerida.',
              error: 'Hora inválida.',
              value: '{{value}} hs',
            },
            endTime: {
              title: 'Hasta',
              required: 'La hora "hasta" es requerida.',
              error: 'Hora inválida.',
              value: '{{value}} hs',
            },
            instructions: {
              title: 'Instrucciones',
              isSpecial: 'Es especial?',
              text: 'Texto',
              language: 'Idioma',
              edit: 'Editar instrucción',
            },
          },
          update: {
            success: 'Check In / Out actualizado correctamente',
            error:
              'Ha ocurrido un error actualizando las opciones de Check In / Out. Contacte al administrador del sitio',
          },
        },
        location: {
          tab: 'Location',
          title: 'Mi ubicación',
          showInMap: 'Usar ubicación',
          editor: {
            latLong: {
              required: 'La latitud y longitud son obligatorias',
              error: 'Formato de latitud y longitud inválido',
            },
          },
          update: {
            success: 'Ubicación actualizadas correctamente',
            error: 'Ha ocurrido un error actualizando la ubicación. Contacte al administrador del sitio',
          },
        },
        rooms: {
          tab: 'Habitaciones',
          title: 'Mis habitaciones',
          subTitle: 'Habitación {{code}}',
        },
        images: {
          tab: 'Imágenes',
          title: 'Imágenes del Hotel',
          tags: 'Etiquetas de la habitación',
          update: {
            success: 'Imágenes actualizadas correctamente',
            error: 'Ha ocurrido un error actualizando las imágenes. Contacte al administrador del sitio',
          },
        },
        amenities: {
          tab: 'Amenitites',
          title: 'Amenitites del Hotel',
          update: {
            success: 'Amenitites actualizadas correctamente',
            error: 'Ha ocurrido un error actualizando las amenities. Contacte al administrador del sitio',
          },
        },
        moderation: {
          link: {
            button: 'Asociar',
            success: 'Hotel asociado correctamente',
            error: 'Ha ocurrido un error asociando los hoteles. Contactacte al administrador del sitio',
          },
          unlink: {
            button: 'Desasociar',
            success: 'Hotel desasociado correctamente',
            error: 'Ha ocurrido un error desasociando los hoteles. Contactacte al administrador del sitio',
          },
          moderate: {
            button: "Marcar como '{{status}}'",
            success: "Hotel marcado como '{{status}}'",
            error: "Ha ocurrido un error marcando el hotel como '{{status}}'. Contactacte al administrador del sitio",
          },
          linked: {
            title: 'Asociado',
          },
        },
        priority: {
          label: 'Prioridad',
          success: 'Prioridad actualizada a {{priority}}',
          error: 'Ha ocurrido un error actualizando la prioridad',
        },
      },
    },
    amenitiesTitle: 'Comodidades',
    amenities: {
      LGBTQ: 'LGBTQ',
      ac: 'Aire acondicionado',
      allInclusive: 'All Inclusive',
      aquaGym: 'Aqua Gym',
      arte: 'Arte',
      azafata: 'Azafata',
      babyFriendly: 'Bebés',
      badminton: 'Bádminton',
      bakery: 'Panadería',
      barDeTragos: 'Bar de tragos',
      basketball: 'Básquetbol',
      beach: 'Playa',
      beerPaint: 'Cervecería',
      bicycle: 'Bicicletas',
      billar: 'Mesa de billar',
      botSail: 'Vela',
      breakfast: 'Desayuno incluido',
      brillos: 'Fuegos artificiales',
      brunch: 'Brunch',
      carRental: 'Alquiler de autos',
      cardFill: 'Tarjeta de crédito',
      casino: 'Casino',
      chefCocina: 'Chef',
      childCare: 'Guardería para niños',
      cleaningService: 'Servicio de limpieza',
      closet: 'Clóset',
      concierge: 'Conserje',
      conferenceRoom: 'Salón de conferencias',
      currencyExchange: 'Casa de cambio',
      deck: 'Deck',
      desfibrilator: 'Desfibrilador',
      doctor: 'Doctor',
      dryCleaning: 'Lavandería en seco',
      dryer: 'Secadora',
      eGames: 'eGames',
      ecoFriendly: 'Eco-friendly',
      edificio: 'Edificio',
      elevator: 'Ascensor',
      entertainment: 'Entretenimiento',
      family: 'Familiar',
      fireplace: 'Chimenea',
      girlGym: 'Gimnasia para mujeres',
      golf: 'Golf',
      grillBbc: 'Barbacoa',
      gym: 'Gimnasio',
      hairSaloon: 'Peluquería',
      jacuzzi: 'Jacuzzi',
      laundry: 'Lavandería',
      library: 'Librería',
      luggageCart: 'Carrito para valijas',
      meetingRoom: 'Sala de reuniones',
      mobileOk: 'Señal para móvil',
      moneyOff: 'Efectivo',
      multipleCard: 'Varias tarjetas',
      nightClub: 'Club nocturno',
      nightLife: 'Vida nocturna',
      parking: 'Estacionamiento',
      petfriendly: 'Pet friendly',
      phone: 'Teléfono',
      photocamara: 'Fotografía',
      pingPong: 'Ping Pong',
      pool: 'Piscina',
      poolTable: 'Mesa de pool',
      proyector: 'Proyector',
      restaurant: 'Servicio de restaurante',
      roomService: 'Servicio a la habitación',
      safeBox: 'Caja fuerte',
      sauna: 'Sauna',
      scubadiving: 'Buceo',
      securityCam: 'Cámaras de seguridad',
      seguridad: 'Seguridad',
      shop: 'Tienda',
      smokeDetector: 'Detector de humo',
      smokeFree: 'Libre de humo',
      smoking: 'Fumadores',
      solarium: 'Solárium',
      spa: 'Spa',
      sportsDeportes: 'Deportes',
      starFilled: 'Estrella',
      surf: 'Surf',
      tennis: 'Tenis',
      transfer: 'Traslados',
      tv: 'Televisión',
      valetParking: 'Valet parking',
      veggie: 'Vegetarianos',
      volley: 'Voleibol',
      volunteerActivism: 'Voluntariado',
      waterSki: 'Esquí acuático',
      wheelChair: 'Acceso para silla de ruedas',
      wifi: 'WiFi',
      notMappedYet: 'El amenity no ha sido mapeado aún.',
    },
    categories: {
      [HotelCategories.AGROTOURISM]: 'Agroturismo',
      [HotelCategories.ONE_STAR]: 'Una Estrella',
      [HotelCategories.ONE_KEY]: 'Una Llave',
      [HotelCategories.TWO_STARS]: 'Dos Estrellas',
      [HotelCategories.TWO_KEYS]: 'Dos Llaves',
      [HotelCategories.THREE_STARS]: 'Tres Estrellas',
      [HotelCategories.THREE_KEYS]: 'Tres Llaves',
      [HotelCategories.FOUR_STARS]: 'Cuatro Estrellas',
      [HotelCategories.FOUR_KEYS]: 'Cuatro Llaves',
      [HotelCategories.FOUR_STARS_LUXURY]: 'Cuatro Estrellas Lujo',
      [HotelCategories.FIVE_STARS]: 'Cinco Estrellas',
      [HotelCategories.FIVE_KEYS]: 'Cinco Llaves',
      [HotelCategories.FIVE_STARS_LUXURY]: 'Cinco Estrellas Lujo',
      [HotelCategories.ONE_STAR_APARTHOTEL]: 'Apartotel Una Estrella',
      [HotelCategories.TWO_STARS_APARTHOTEL]: 'Apartotel Dos Estrellas',
      [HotelCategories.THREE_STARS_APARTHOTEL]: 'Apartotel Tres Estrellas',
      [HotelCategories.FOUR_STARS_APARTHOTEL]: 'Apartotel Cuatro Estrellas',
      [HotelCategories.FIVE_STARS_APARTHOTEL]: 'Apartotel Cinco Estrellas',
      [HotelCategories.FIRST_CATEGORY_APARTMENT]: 'Apartamento de Primera Categoría',
      [HotelCategories.SECOND_CATEGORY_APARTMENT]: 'Apartamento de Segunda Categoría',
      [HotelCategories.THIRD_CATEGORY_APARTMENT]: 'Apartamento de Tercera Categoría',
      [HotelCategories.BED_AND_BREAKFAST]: 'Cama y Desayuno',
      [HotelCategories.THREE_STARS_BED_AND_BREAKFAST]: 'Cama y Desayuno Tres Estrellas',
      [HotelCategories.FOUR_STARS_BED_AND_BREAKFAST]: 'Cama y Desayuno Cuatro Estrellas',
      [HotelCategories.FIVE_STARS_BED_AND_BREAKFAST]: 'Cama y Desayuno Cinco Estrellas',
      [HotelCategories.BOUTIQUE]: 'Boutique',
      [HotelCategories.FIRST_CATEGORY_CAMPING]: 'Camping de Primera Categoría',
      [HotelCategories.SECOND_CATEGORY_CAMPING]: 'Camping de Segunda Categoría',
      [HotelCategories.GUESTHOUSE]: 'Casa de Huéspedes',
      [HotelCategories.ONE_AND_HALF_STARS]: 'Una Estrella y Media',
      [HotelCategories.SUPERIOR_TWO_STARS]: 'Dos Estrellas Superior',
      [HotelCategories.TWO_AND_HALF_STARS]: 'Dos Estrellas y Media',
      [HotelCategories.SUPERIOR_THREE_STARS]: 'Tres Estrellas Superior',
      [HotelCategories.THREE_AND_HALF_STARS]: 'Tres Estrellas y Media',
      [HotelCategories.FOUR_AND_HALF_STARS]: 'Cuatro Estrellas y Media',
      [HotelCategories.FIVE_AND_HALF_STARS]: 'Cinco Estrellas y Media',
      [HotelCategories.LUXURY_HISTORIC_HOTEL]: 'Hotel Histórico de Lujo',
      [HotelCategories.SUPERIOR_CATEGORY_RURAL_HOTEL]: 'Hotel Rural Categoría Superior',
      [HotelCategories.ONE_STAR_HOSTEL]: 'Hostal Una Estrella',
      [HotelCategories.TWO_STARS_HOSTEL]: 'Hostal Dos Estrellas',
      [HotelCategories.THREE_STARS_HOSTEL]: 'Hostal Tres Estrellas',
      [HotelCategories.FOUR_STARS_HOSTEL]: 'Hostal Cuatro Estrellas',
      [HotelCategories.FIVE_STARS_HOSTEL]: 'Hostal Cinco Estrellas',
      [HotelCategories.ONE_STAR_RURAL_HOSTEL]: 'Hostal Rural Una Estrella',
      [HotelCategories.TWO_STARS_RURAL_HOSTEL]: 'Hostal Rural Dos Estrellas',
      [HotelCategories.LODGE]: 'Lodge',
      [HotelCategories.MINI_HOTEL]: 'Mini Hotel',
      [HotelCategories.PENDING_CATEGORY]: 'Categoría Pendiente',
      [HotelCategories.PENSION]: 'Pensión',
      [HotelCategories.POUSADA]: 'Posada',
      [HotelCategories.RESIDENCE]: 'Residencia',
      [HotelCategories.RESORT]: 'Resort',
      [HotelCategories.WITHOUT_OFFICIAL_CATEGORY]: 'Sin Categoría Oficial',
      [HotelCategories.STANDARD]: 'Estándar',
      [HotelCategories.FOUR_SUPERIOR_STARS]: 'Cuatro Estrellas Superior',
      [HotelCategories.VILLA]: 'Villa',
      [HotelCategories.VACATION_TOURIST_HOMES]: 'Viviendas Turísticas Vacacionales',
    },
    hotelImageTag: {
      title: 'Tags',
      subtitle: 'Tags disponibles',
      results: 'Resultados',
      search: 'Buscar',
      table: {
        name: 'Nombre',
        color: 'Color',
      },
      create: {
        createdSuccess: 'Tag creado',
        createdError: 'Ocurrio un error al crear el tag',
        title: 'Crear Tag',
        name: 'Nombre',
        color: 'Color',
      },
      edit: {
        createdSuccess: 'Tag actualizado',
        createdError: 'Ocurrio un error al actualizar el tag',
        title: 'Editar tag',
        name: 'Nombre',
        color: 'Color',
      },
      delete: {
        success: 'Tag eliminada',
        error: 'Ocurrió un error al eliminar el tag',
        title: 'Eliminar tag',
        message: 'Seguro desear eliminar: {{ name }}?',
      },
    },
    paymentPolicies: {
      nonAnticipate: 'Sin pago adelantado',
    },
    cancelationPolicies: {
      free: 'Cancelación gratis',
    },
  },
  users: {
    results: 'resultados',
    search: 'Buscar usuario',
    create: {
      title: 'Crear usuario',
      createdError: 'Error al crear el usuario',
      warning: 'Esta es la única vez donde se va a mostrar el password! Guárdalo bien!.',
      user: 'Usuario:',
      password: 'Password:',
      finish: 'Terminar',
    },
    selector: {
      title: 'Selección de usuarios',
    },
    list: {
      title: 'Usuarios',
      subtitle: 'Listado de usuarios',
    },
    table: {
      name: 'Nombre Completo',
      email: 'Email',
      agency: 'Agencia',
      roles: 'Roles',
      client: 'Vista como cliente',
    },
    view: {
      teams: 'equipos',
    },
  },
  clients: {
    selector: {
      title: 'Selector de Cliente',
    },
    title: 'Clientes',
    subtitle: 'Lista de clientes',
    results: 'Resultados',
    search: 'Buscar',
    table: {
      name: 'Nombre',
      email: 'Correo',
      phone: 'Teléfono #',
      agency: 'Agencia',
      whatsapp: 'WhatsApp #',
      client: 'Cliente',
      contact: 'Contacto',
      preferredAgent: 'Agente Preferido',
      organization: 'Organización',
      location: 'Ubicación',
      created: 'Creado',
    },
    filters: {
      clientName: 'Nombre del Cliente',
      countries: 'Países',
      organizations: 'Organizaciones',
      agencies: 'Agencias',
      preferredAgent: 'Agente Preferido',
      documentTypes: 'Tipos de Documento',
      searchClientNamePlaceholder: 'Buscar por nombre o apellido',
      myClients: 'Ver solo mis clientes',
    },
    view: {
      title: 'Información del Cliente',
      agencyInfo: 'Información de Agencia',
      basicInfo: 'Información Básica',
      contactInfo: 'Información de Contacto',
      addressInfo: 'Información de Dirección',
      documentInfo: 'Información de Documento',
      agency: 'Agencia',
      firstName: 'Nombre',
      lastName: 'Apellido',
      email: 'Correo electrónico',
      phoneCountryCode: 'Código de País',
      phoneNumber: 'Número de Teléfono',
      whatsappNumber: 'Número de WhatsApp',
      country: 'País',
      city: 'Ciudad',
      postalCode: 'Código Postal',
      address1: 'Dirección',
      address2: 'Dirección (línea 2)',
      documentType: 'Tipo de Documento',
      documentNumber: 'Número de Documento',
      clientSince: 'Cliente desde',
      basicInfoSuccess: 'Información básica actualizada',
      contactInfoSuccess: 'Información de contacto actualizada',
      addressInfoSuccess: 'Información de dirección actualizada',
      documentInfoSuccess: 'Información de documento actualizada',
      createdError: 'Hubo un error al actualizar la información',
      preferredAgent: 'Agente preferido',
      selectPreferredAgent: 'Seleccionar agente preferido',
    },
    errors: {
      DUPLICATED_PHONE_ERROR: 'El teléfono ya está registrado para la Agencia seleccionada',
      DUPLICATED_EMAIL_ERROR: 'El correo ya está registrado para la Agencia seleccionada',
      DUPLICATED_WHATSAPP_ERROR: 'El número de WhatsApp ya está registrado para la Agencia seleccionada',
      MISSING_FIELDS_ERROR: 'Debes proporcionar un correo, teléfono o número de WhatsApp',
    },
    create: {
      createdSuccess: 'Cliente creado exitosamente',
      createdError: 'Hubo un error al crear el cliente',
      createdErrorPhone: 'El teléfono ya está registrado para la Agencia seleccionada',
      createdErrorEmail: 'El correo ya está registrado para la Agencia seleccionada',
      createdErrorWhatsapp: 'El número de WhatsApp ya está registrado para la Agencia seleccionada',
      createdErrorMissingFields: 'Debes proporcionar un correo, teléfono o número de WhatsApp',
      title: 'Crear Cliente',
      firstName: 'Nombre',
      agency: 'Agencia',
      lastName: 'Apellido',
      email: 'Correo electrónico',
      phoneNumber: 'Teléfono',
      phoneCountryCode: 'Código de País',
      country: 'País',
      city: 'Ciudad',
      postalCode: 'Código Postal',
      documentType: 'Tipo de Documento',
      documentNumber: 'Número de Documento',
      address1: 'Dirección',
      address2: 'Dirección (línea 2)',
      whatsappNumber: 'WhatsApp',
    },
    edit: {
      whatsappNumber: 'WhatsApp',
      createdSuccess: 'Cliente actualizado exitosamente',
      createdError: 'Hubo un error al actualizar el cliente',
      title: 'Actualizar Cliente',
      firstName: 'Nombre',
      agency: 'Agencia',
      lastName: 'Apellido',
      email: 'Correo electrónico',
      phoneNumber: 'Teléfono',
      phoneCountryCode: 'Código de País',
      country: 'País',
      city: 'Ciudad',
      postalCode: 'Código Postal',
      documentType: 'Tipo de Documento',
      documentNumber: 'Número de Documento',
      address1: 'Dirección',
      address2: 'Dirección (línea 2)',
    },
  },
  workflows: {
    title: 'Flujos de trabajo',
    subtitle: 'Listado de flujos de trabajo',
    results: 'Resultados',
    search: 'Buscar',
    form: {
      name: 'Nombre',
      color: 'Color',
      domains: 'Dominios',
      domainsHint: 'Puedes agregar dominios o emails completos, ejemplo: *@acme.com o john.doe@acme.com',
      error: {
        invalidEmailDomain: 'El dominio {{ emailDomainValue }} no es válido',
      },
    },
    selectedStep: {
      title: 'Detalle del paso',
      label: 'Nombre del paso',
      info: 'Información del paso',
      delete: 'Eliminar paso',
      teams: 'Equipos',
      mailTemplate: 'Plantilla de correo',
      isCancelStepLabel: 'Paso de cancelación?',
      cancelStepExists: 'Ya existe un paso de cancelación',
    },
    instructions: {
      title: 'Instrucciones para {{step}}',
      save: 'Guardar',
      close: 'Cerrar',
      tabs: {
        edit: 'Edición',
        preview: 'Previsualización',
      },
    },
    actions: {
      title: 'Acciones',
      filters: 'Filtros',
      settings: 'Configuraciones',
      add: 'Agregar',
    },
    table: {
      name: 'Nombre',
      color: 'Color',
      emailDomains: 'Dominios de email',
    },
    view: {
      addInitialStep: 'Agregar paso inicial',
      addMiddleStep: 'Agregar paso intermedio',
      addFinalStep: 'Agregar paso final',
      agency: 'Agencia',
      color: 'Color',
      created: 'Creado',
      updatedSuccess: 'Flujo de trabajo actualizado',
    },
    edit: {
      title: 'Editar Flujo de trabajo',
      error: 'Ocurrió un error al actualizar el flujo de trabajo',
      success: 'Flujo de trabajo actualizado',
      unsavedChanges: 'Hay cambios sin guardar. Quieres guardarlos antes de salir?',
    },
    create: {
      title: 'Crear flujo de trabajo',
      error: 'Ocurrió un error al crear el flujo de trabajo',
      success: 'Flujo de trabajo creado!',

      step: {
        name: 'Nombre',
        title: 'Crear paso',
        color: 'Color',
        teams: 'Equipos',
      },
    },
  },
  agencies: {
    title: 'Agencias',
    subtitle: 'Listado de agencias',
    results: 'Resultados',
    search: 'Buscar',
    view: {
      dashboard: 'Dashboard',
      caseHandlers: 'Manejadores de casos',
      generalSettings: 'Configuración General',
      teams: 'Equipos',
      communications: 'Comunicaciones',
      callsMenu: 'Menú de llamadas',
      iaConfig: 'Configuración de IA',
      whatsappSettings: 'Configuración de Whatsapp',
      caseTriggers: 'Disparadores de casos',
      uploads: 'Subir data',
      workflows: 'Flujos de trabajo',
      organizations: 'Organizaciones',
      settings: 'Configuracion',
      supportForms: 'Support Forms',
      flights: 'Aire',
      hotels: 'Hoteles',
      markupRules: 'Reglas de Markup',
      routingRules: 'Reglas de Ruteo',
      invalidationRules: 'Reglas de invalidación',
      integrations: 'Integraciones',
      customFields: 'Campos Personalizados',
    },
    settings: {
      twilio: 'Twilio',
      saveSuccess: 'Settings guardados',
      saveError: 'Error al guardar los settings',
      general: 'General',
      auth: 'Autenticación',
      communicationChannels: 'Canales de communicación',
      maps: 'Mapas',
      whitelabeling: 'Etiqueta blanca',
      aws: 'AWS',
      sendgrid: 'Sendgrid',
      googleMaps: 'Google Maps',
      callsMenu: 'Menú de llamadas',
      whatsappSettings: 'Configuración de Whatsapp',
      phone: 'Telefonía',
      email: 'Correo',
      googleSignIn: 'Google Sign-in',
      registration: 'Registro',
      whatsapp: 'Whatsapp (Gupshup)',
      whatsappSubscriptionError: 'Error al crear subscripcion',
      emailSignature: 'Firma de correo',
      openai: 'OpenAI',
      ai: 'Inteligencia Artificial',
      hotels: 'Hotels',
      placeholders: {
        selectAIUser: 'Selecciona un usuario IA',
        noUsersFound: 'No se encontraron usuarios',
        contextMessages: '10',
      },
      envMap: {
        AI_CASE_HANDLERS_ENABLED: 'Habilitar IA para manejadores de casos',
        IA_USER_ID: 'ID de Usuario IA',
        TWILIO_ACCOUNT_SID: 'Account SID',
        TWILIO_AUTH_TOKEN: 'Authorization Token',
        TWILIO_API_KEY: 'API KEY',
        TWILIO_API_KEY_SECRET: 'Api KEY secret',
        TWILIO_TWIML_APP_SID: 'Twiml app SID',
        TWILIO_PHONE_NUMBER: 'Numero de telefono',
        TWILIO_TRUNK_SID: 'Twilio trunk SID',
        JWT_SECRET: 'JWT secret',
        DEFAULT_WORKFLOW_ID: 'Default workflow Id',
        WHATSAPP_DEFAULT_WORKFLOW_ID: 'Flujo de trabajo predeterminado Whatsapp',
        WHATSAPP_PREFERRED_AGENT_WORKFLOW_ID: 'Flujo de trabajo para agente preferido WhatsApp',
        REFRESH_JWT_SECRET: 'JWT refresh token secret',
        JWT_RESET_PASSWORD: 'JWT reset pwd token secret',
        WHITELABEL_CODE: 'Codigo de Whitelabel',
        AWS_REGION: 'Region',
        AWS_ACCESS_KEY_ID: 'Access Key Id',
        AWS_SECRET_ACCESS_KEY: 'Secret Access Key',
        AWS_S3_ENDPOINT: 'S3 endpoint',
        AWS_S3_BUCKET_EMAIL_ATTACHMENTS: 'S3 bucket mail attachments',
        SENDGRID_API_KEY: 'API Key',
        SENDGRID_SENDER_EMAIL: 'Mail emisor',
        SENDGRID_INBOUND_MAILS: 'Casillas gestionadas',
        VITE_GOOGLE_KEY: 'Google Key',
        VITE_MAP_ID: 'Map Id',
        GOOGLE_CLIENT_ID: 'Client ID',
        GOOGLE_CLIENT_SECRET: 'Client Secret',
        GOOGLE_CALLBACK_URL: 'Callback URL',
        CLIENT_URL: 'Client URL',
        GUPSHUP_AUTH_SECRET: 'Auth Secret',
        GUPSHUP_AUTH_USER: 'Mail de login',
        MAIL_CREDENTIAL: 'Credenciales de email',
        GUPSHUP_AUTH_TOKEN: 'Auth Token',
        GUPSHUP_APP_ID: 'ID de applicación',
        GUPSHUP_PARTNER_API_URL: 'Partner Api URL',
        GUPSHUP_APP_NAME: 'Nombre de aplicación',
        GUPSHUP_PHONE_NUMBER: 'Número telefónico',
        GUPSHUP_SENDER_NAME: 'Nombre de quién envía el mensaje',
        GUPSHUP_API_KEY: 'API Key',
        ALLOWED_EMAIL_DOMAIN: 'Dominio de email permitido para acceder',
        VITE_MANUAL_REGISTRATION_ALLOWED: 'Registro manual activado? (true/false)',
        WHATSAPP_OPENAI_API_KEY: 'Clave API de OpenAI',
        WHATSAPP_OPENAI_AMOUNT_OF_CONTEXT_MESSAGES: 'Cantidad de mensajes de contexto para la IA',
        HOTEL_BOOK_AGENCY_REFERENCE: 'Prefijo de referencia de reserva de hoteles',
        HOTEL_BOOK_MINOR_AGE_DELIMITER: 'Determinar edade de menores',
      },
    },
    table: {
      name: 'Nombre',
      currency: 'Moneda',
      country: 'Pais',
      isPrivate: 'Es un sitio privado',
    },
    create: {
      createdSuccess: 'Agencia creada!',
      createdError: 'Ocurrio un error al crear la agencia',
      title: 'Crear Agencia',
      name: 'Nombre',
      country: 'Pais',
      currency: 'Moneda',
      isPrivate: 'Es un sitio privado',
    },
    edit: {
      createdSuccess: 'Agencia actualizada!',
      createdError: 'Ocurrió un error al actualizar la agencia',
      title: 'Editar Agencia',
      name: 'Nombre',
      country: 'País',
      currency: 'Moneda',
      isPrivate: 'Es un sitio privado',
    },
  },
  caseHandlers: {
    title: 'Manejadores de casos',
    subtitle: 'Listado de manejadores de casos',
    results: 'Resultados',
    search: 'Buscar',
    table: {
      name: 'Nombre',
      description: 'Descripción',
      enabled: 'Habilitado',
      scanWorkflow: 'Flujo de escaneo',
      scanStep: 'Paso de escaneo',
      scanStatus: 'Estado de escaneo',
      nextWorkflow: 'Siguiente flujo',
      nextStep: 'Siguiente paso',
      nextStatus: 'Siguiente estado',
      channel: 'Canal',
    },
    edit: {
      title: 'Editar Manejador de Casos',
      success: 'Manejador actualizado exitosamente',
      error: 'Ocurrió un error al actualizar el manejador',
    },
    create: {
      title: 'Crear Manejador de Casos',
      success: 'Manejador creado exitosamente',
      error: 'Ocurrió un error al crear el manejador',
    },
    form: {
      name: 'Nombre',
      namePlaceholder: 'Ingrese el nombre del manejador',
      description: 'Descripción',
      descriptionPlaceholder: 'Ingrese una descripción opcional',
      messageContextHistoryCount: 'Cantidad de contexto',
      enabled: 'Habilitado',
      channel: 'Canal',
      channelPlaceholder: 'Ingrese el canal (opcional)',
      scanStatus: 'Estado de escaneo',
      scanStatusPlaceholder: 'Ingrese el estado de escaneo (opcional)',
      nextStatus: 'Siguiente estado',
      nextStatusPlaceholder: 'Ingrese el siguiente estado (opcional)',
      scanWorkflow: 'Workflow de escaneo',
      scanWorkflowPlaceholder: 'Seleccione workflow de escaneo',
      scanStep: 'Paso de escaneo',
      scanStepPlaceholder: 'Seleccione paso de escaneo',
      nextWorkflow: 'Siguiente workflow',
      nextWorkflowPlaceholder: 'Seleccione siguiente workflow',
      nextStep: 'Siguiente paso',
      nextStepPlaceholder: 'Seleccione siguiente paso',
      prompt: 'Prompt',
      promptPlaceholder: 'Ingrese el prompt del manejador',
      listeningTo: 'Escuchando',
      onSuccess: 'En caso de éxito',
      onBailout: 'En caso de bailout',
      onCancel: 'En caso de cancelación',
      promptSection: 'Prompt',
      // Nuevas traducciones para los campos del IA Handler
      iaHandlerConfiguration: 'Configuración del Handler IA',
      handlerPurpose: 'Propósito del Handler',
      handlerPurposePlaceholder: 'Describe el propósito específico de este handler...',
      behaviorRules: 'Reglas de Comportamiento',
      behaviorRulesPlaceholder: 'Define las reglas específicas de comportamiento...',
      stepsDescription: 'Descripción de Pasos',
      stepsDescriptionPlaceholder: 'Describe los pasos que debe seguir...',
      examples: 'Ejemplos',
      handlerExample: 'Ejemplo de Resultado',
      handlerExamplePlaceholder: 'Proporciona un ejemplo del resultado esperado...',
      conversationExample: 'Ejemplo de Conversación',
      conversationExamplePlaceholder: 'Muestra un ejemplo de conversación...',
      validationsAndNotes: 'Validaciones y Notas',
      validations: 'Validaciones',
      validationsPlaceholder: 'Define las validaciones requeridas...',
      allowedTools: 'Herramientas Permitidas',
      allowedToolsPlaceholder: 'Selecciona las herramientas que puede usar este handler',
      additionalNotes: 'Notas Adicionales',
      additionalNotesPlaceholder: 'Agrega cualquier nota adicional importante...',
      // Tabs labels
      tabs: {
        listening: 'Escuchando',
        handlePurpose: 'Propósito del Handler',
        behaviourRules: 'Reglas de Comportamiento',
        steps: 'Pasos',
        resultExamples: 'Ejemplos de Resultados',
        conversationExamples: 'Ejemplos de Conversación',
        validations: 'Validaciones',
        additionalNotes: 'Notas Adicionales',
      },
    },
  },
  organizations: {
    title: 'Organizaciones',
    subtitle: 'Organizaciones disponibles',
    results: 'Resultados',
    search: 'Buscar',
    table: {
      name: 'Nombre',
      domain: 'Dominio',
      agency: 'Agencia',
      memberCount: 'Cantidad de miembros',
    },
    create: {
      createdSuccess: 'Organización creada',
      createdError: 'Ocurrió un error al crear organización',
      title: 'Crear Organización',
      name: 'Nombre',
      domain: 'Dominio',
    },
    edit: {
      createdSuccess: 'Organización actualizada correctamente',
      createdError: 'Ocurrio un error al actualizar la organización',
      title: 'Editar Organización',
      name: 'Nombre',
      domain: 'Dominio',
    },
    domainError: 'Ingresa un domino válido (e.g. @tudominio.com)',
    members: 'miembros',
    linkUserToOrganization: {
      success: 'Usuario asociado correctamente',
      failed: 'Un problema al asociar al usuario',
      removed: 'Usuario removido de la organización',
      removedFailed: 'Ocurrio un error al remover el usuario',
      removeToolip: 'Remover de la organización',
    },
    selector: {
      add: 'agregar',
      title: 'Seleccion de Organización',
    },
  },
  teams: {
    title: 'Equipos',
    subtitle: 'Equipos disponibles',
    results: 'Resultados',
    search: 'Buscar',
    table: {
      name: 'Nombre',
      email: 'Email',
      agency: 'Agencia',
      color: 'Color',
      isAvailableInPhone: 'atiende llamadas ',
    },
    create: {
      createdSuccess: 'Equipo creado',
      createdError: 'Ocurrio un error al crear el equipo',
      title: 'Crear Equipo',
      name: 'Nombre',
      agency: 'Agencia',
      color: 'Color',
    },
    edit: {
      createdSuccess: 'Equipo actualizado',
      createdError: 'Ocurrio un error al actualizar el equipo',
      title: 'Editar Equipo',
      name: 'Nombre',
      agency: 'Agencia',
      color: 'Color',
    },
    members: 'miembros',
    linkUserToTeam: {
      success: 'Usuario asociado correctamente',
      failed: 'Un problema al asociar al usuario',
      removed: 'Usuario Removido del equipo',
      removedFailed: 'Ocurrio un error al Remover el usuario',
      removeToolip: 'Remover del equipo',
    },
  },
  error404: {
    message: 'La pagina que buscas no se encontro',
    btnLabel: 'Volver al incio',
  },
  error401: {
    message: 'Acceso no autorizado.',
    btnLabel: 'Volver al incio',
  },
  linkCaseModal: {
    newCase: 'Nuevo caso',
    linkedTo: 'Relacionar con',
    duplicates: 'Duplica a',
    linkSuccess: 'Caso linkeado exitosamente',
    linkError: 'Fallo al linkear el caso',
    selectCaseError: 'Precisas selecionar un caso',
    searchTextLabel: 'Buscá un caso',
  },
  cases: {
    status: {
      [CaseStatus.open]: 'abierto',
      [CaseStatus.inProgress]: 'en progreso',
      [CaseStatus.needsClientAction]: 'esperando respuesta',
      [CaseStatus.needsAgentAction]: 'necesita agente',
      [CaseStatus.done]: 'completado',
      [CaseStatus.canceled]: 'cancelado',
      any: 'Cualquier estado (por defecto: abierto)',
    },
    originatedByChannel: {
      [OriginatedByChannel.WHATSAPP]: 'WhatsApp',
      [OriginatedByChannel.CALL]: 'Llamada',
      [OriginatedByChannel.EMAIL]: 'Correo electrónico',
      [OriginatedByChannel.MANUAL]: 'Manual',
      [OriginatedByChannel.EXTERNAL_INTEGRATION]: 'Integración externa',
      [OriginatedByChannel.UPLOADED]: 'Upload',
      [OriginatedByChannel.SUPPORT_FORM]: 'Formulario de soporte',
    },
    stats: {
      byDay: 'Casos por día',
      count: 'Número de casos',
      date: 'Fecha',
      byTeam: 'Casos por equipo',
      byStatus: 'Casos por estado',
      byWorkflow: 'Casos por workflow',
      noTeam: 'Sin equipo',
      noWorkflow: 'Sin asignar',
    },
    statistics: {
      title: 'Estadísticas de Casos',
      byAgent: 'Casos por Agente',
      byTeam: 'Casos por Equipo',
      byAgency: 'Casos por Agencia',
      byChannel: 'Casos por Canal',
      byStatus: 'Casos por Estado',
      byWorkflow: 'Casos por Flujo',
      byStep: 'Casos por Paso',
    },
    export: {
      csvButton: 'Generar reporte CSV (recibirás un email cuando esté listo)',
      processingMessage: 'Reporte de casos en proceso. Recibirás un email cuando esté listo.',
      errorMessage: 'Hubo un error al generar el reporte',
    },
    create: {
      createdSuccess: 'Case creado exitosamente',
      createdError: 'Ocurrió un error al crear el caso',
      placeholder: 'Ingrese un asunto para el caso...',
      agency: 'Agencia',
      client: 'Cliente',
    },
    update: {
      success: 'Caso actualzado',
      error: 'Ocurrió un error al actualizar el caso',
    },
    list: {
      bulk: {
        title: 'Edicion masiva',
        selectedCount: '{{count}} seleccionados',
        clear: 'Limpiar',
        apply: 'Aplicar',
        leaveUnchanged: 'Sin cambios',
        unassigned: 'Sin asignar',
        workflow: 'Flujo',
        step: 'Paso',
        status: 'Estado',
        agent: 'Agente',
        updatedSuccess: 'Actualizados {{count}} casos',
        updatedError: 'Error al actualizar casos',
        workflowRequired: 'Selecciona un flujo antes de elegir un paso',
        noChanges: 'Selecciona al menos un campo para actualizar',
      },
      filters: {
        status: 'Status',
        title: 'Filtros',
        name: 'Nombre del filtro',
        placeHolderNew: 'Ingrese nombre del nuevo filtro',
        placeHolderExisting: 'Editar nombre del filtro',
        owner: 'Autor',
        myFilters: 'Mis Filtros',
        publicFilters: 'Filtros Públicos',
        checkbox: 'Marcar como público',
        clear: 'Limpiar',
        save: 'Guardar',
        createNew: 'Crear Nuevo',
        createError: 'Error al crear fitlro',
      },
      filterByStatus: 'Filtrar por estado',
      filterByTeam: 'Filtrar por equipo',
      filterByWorkflow: 'Filtrar por flujo',
      filterByStep: 'Filtrar por paso',
      filterByAgent: 'Filtrar por agente',
      filterByClient: 'Filtrar por cliente',
      filterByOrganization: 'Filtrar por organizacion',
      filterBySubject: 'Filtrar por asunto',
      filterByOriginatedByChannel: 'Filtrar por canal de origen',
      filterByAgency: 'Filtrar por agencia',
      filterByCreated: 'Filtrar por fecha de creación',
      filterByUpdated: 'Filtrar por fecha de actualización',
      filterByCreatedAfter: 'Creado despues de',
      filterByCreatedBefore: 'Creado antes de',
      title: 'Casos',
      subtitle: 'Lista de casos',
      tabs: {
        all: 'Todos',
        myGroups: 'Mis Grupos',
        myCases: 'Mis Casos',
      },
      search: 'Buscar...',
      results: 'Resultados',
      table: {
        organization: 'Organización',
        client: 'Cliente',
        subject: 'Asunto',
        agency: 'Agencia',
        workflow: 'Flujo',
        step: 'Paso',
        status: 'Estado',
        channel: 'Canal',
        team: 'Equipo',
        agent: 'Agente',
        created: 'Creado',
        lastUpdate: 'Actualizado',
      },
    },
    view: {
      assignToMe: 'Asignarme a mi',
      case: 'Caso',
      copyCaseNumber: 'Copiar número de caso',
      copySuccess: 'Número de caso copiado al portapapeles',
      titleStatus: 'Estado del caso',
      titleClient: 'Información del cliente',
      titleOtherDetails: 'Detalles adicionales',
      relationRemovedSuccess: 'Relacion removida con exito',
      relationRemovedError: 'Error remover la relacion',
      addRelationShip: 'Agregar relacion a otro caso',
      workflow: 'Flujo',
      instructions: 'Instrucciones',
      step: 'Paso',
      status: 'Estado',
      team: 'Equipo',
      organization: 'Organizacion',
      client: 'Cliente',
      agent: 'Agente Asignado',
      saleorderLegacy: 'Sales Order (Legacy)',
      agency: 'Agencia',
      created: 'Creado',
      iaEnabled: 'IA Habilitada',
      log: {
        UPDATE: 'actualizó',
        CREATE: 'creó',
        LINKED: 'asoció',
        DUPED: 'duplicó',
        WORKFLOW: 'Flujo',
        ORGANIZATION: 'Organización',
        AGENCY: 'Agencia',
        IA_ENABLED: 'IA Habilitada',
        STEP: 'Paso',
        TEAM: 'Equipo',
        STATUS: 'Estado',
        CLIENT: 'Client',
        AGENT: 'Agente',
        CASE: 'Caso',
        DELETE: 'eliminó',
        SALEORDER: 'Sales order',
        from: 'de',
        to: 'a',
        saleorder: {
          DELETE: 'Orden de venta eliminada',
          CREATE: 'Orden de venta agregada',
        },
      },
      content: {
        activity: 'Actividad',
        voicemail: 'Mensaje de voz',
        note: 'Nota',
        search: 'Buscar...',
        whatsapp: 'Whatsapp',
        show: 'Mostrar',
        all: 'Todos',
        history: 'Historial',
        phone: 'Llamadas',
        email: 'E-mails',
        files: 'Archivos',
        notes: 'Notas',
        endsWithin: 'Su sesión finaliza en:',
        subject: 'Asunto',
        date: 'Fecha',
        viewMore: 'Ver más',
        noteText: 'agregó una nota',
        caseEdit: 'Editó el caso',
        caseCreate: 'Creó el caso',
        edit: 'Edición',
        emailText: 'envió un correo electrónico',
        phoneCallText: 'llamó a',
        reply: 'Responder',
        replyToAll: 'Responder a todos',
        from: 'De',
        to: 'Para',
        cc: 'CC',
        bcc: 'BCC',
        newEmail: 'Enviar email',
        draft: 'Borrador',
        newWhatsapp: 'Enviar whatsapp',
        newNotes: 'Crear nota',
        imagePreview: 'Previsualización de la imagen',
        call: 'Llamada',
        incomingCall: 'Llamada entrante',
        outgoingCall: 'Llamada saliente',
        missingContactData: 'No hay datos de contacto disponibles',
        callTransfers: {
          transferredTo: 'Transferido a',
        },
        loading: 'Cargando...',
        hideThread: 'Ocultar hilo',
        showThread: 'Ver hilo anterior',
        showThreadWithCount: 'Ver hilo anterior ({{count}} mensajes)',
        previousMessages: 'Mensajes anteriores • {{count}} mensajes',
        noDate: 'Sin fecha',
        replySubject: 'Re',
        legacyEmailTooltip: 'Email anterior a la optimización de hilos',
        cleanedEmailTooltip: 'Contenido citado detectado y limpiado',
      },
      moreInfo: 'Mas información',
      lessInfo: 'Menos información',
      tabInfo: 'Info',
      tabHistory: 'Historia',
    },
  },
  callsMenu: {
    [WELCOME_MESSAGE]: 'Mensaje de bienvenida',
    saveSuccess: 'IVR guardado correctamente',
    saveError: 'Ocurrió un error al guardar el IVR',
    selectedStage: {
      timeout: 'Tiempo de espera (seg.)',
      timeoutAction: {
        label: 'Si ningun digito es marcado',
        action: {
          [TimeoutAction.REPEAT]: 'Repetir',
          [TimeoutAction.EXIT]: 'Salir',
        },
      },
      dialpad: 'Números a conectar',
      audioRecorder: {
        title: 'Grabar',
        delete: 'Eliminar grabación',
      },
      text: {
        title: 'Texto',
        textPlaceholder: 'Ingrese el texto que desea convertir a voz',
      },
      file: {
        title: 'Archivo',
        uploading: 'Subiendo archivo...',
        delete: 'Eliminar archivo',
        upload: 'Subir archivo',
        uploaded: 'Audio cargado',
        uploadSuccess: 'Archivo cargado correctamente',
        uploadError: 'Ocurrió un error al cargar el archivo',
      },
      audioPlayer: {
        play: 'Reproducir',
        pause: 'Pausar',
        delete: 'Eliminar audio',
      },
      sendTo: 'Enviar a',
      actionType: {
        [IVRActionType.ENTER_EXTENSION]: 'Extensión',
        [IVRActionType.SEND_TO_QUEUE]: 'Equipo',
      },
      communicationType: {
        title: 'Medio de comunicación',
        types: {
          [CommunicationType.TEXT_TO_SPEECH]: 'Texto a voz',
          [CommunicationType.AUDIO_RECORD]: 'Sonido',
          [CommunicationType.AUDIO_FILE]: 'Archivo',
        },
      },
      menu: {
        label: 'Nombre del menú',
        delete: 'Eliminar menú',
      },
      action: {
        label: 'Nombre de la acción',
        delete: 'Eliminar acción',
      },
    },
    stage: {
      team: 'Equipo',
    },
  },
  notifications: {
    title: 'Notificaciones',
    clean: 'Marcar como leidas',
    recents: 'Recientes',
    [NotificationType.CASE_ASSIGNED_TO_ME]: {
      title: 'Caso Asignado',
      message: 'El usuario <b>{{username}}</b> te asigno el caso <b>{{caseId}}</b>',
    },
    [NotificationType.CASE_UNASSIGNED_ME]: {
      title: 'Caso Asignado',
      message: 'El usuario <b>{{username}}</b> quito del caso <b>{{caseId}}</b>',
    },
    [NotificationType.CASE_STATUS_MODIFIED]: {
      title: 'Caso Modificado',
      message: 'El usuario <b>{{username}}</b> modificó el estado del caso <b>{{caseId}}</b> a <b>{{status}}</b>',
    },
    [NotificationType.CASE_NOTE_ADDED]: {
      title: 'Nota Agregado',
      message: 'El usuario <b>{{username}}</b> agregó una nota al caso <b>{{caseId}}</b>',
    },
    [NotificationType.CASE_EMAIL_ADDED]: {
      title: 'Mail enviado',
      message: 'El usuario <b>{{username}}</b> envio un mail en el caso <b>{{caseId}}</b>',
    },
    [NotificationType.CASE_CLIENT_REPLIED]: {
      title: 'El Cliente Contesto',
      message: 'El cliente <b>{{clientName}}</b> contestó en el caso <b>{{caseId}}</b>',
    },
    [NotificationType.USER_NOTIFICATION_TRIGGER]: {
      title: 'Coincidencia de Disparador',
      message: 'El caso <b>{{caseId}}</b> coincidió con tu disparador (estado <b>{{status}}</b>)',
    },
  },
  markupRules: {
    isActive: 'Activa',
    newGroup: '+ NUEVO GRUPO',
    buttonBackList: 'Volver a la lista',
    aplicationRatesTrackers: 'Aplica para Rates Trackers',
    deleteGroup: 'ELiminar grupo',
    addCondition: '+ Agregar condición',
    addAction: '+ Agregar acción',
    relationshipGroups: 'Relación entre grupos',
    and: 'Y',
    or: 'O',
    name: 'Nombre',
    order: 'Orden',
    markup: 'Margen',
    percentage: 'porcentaje',
    fee: 'cargo fijo',
    buttonDiscard: 'Descartar',
    buttonSave: 'Guardar',
    conditionalGroup: 'Grupo condicional',
    createSuccess: 'Regla creada',
    createError: 'Hubo un error al crear la regla',
    viewEdit: {
      titleNew: 'Crear margen',
      titleEdit: 'Editar margen',
      subtitleNew: 'Crear margen',
      subtitleEdit: 'Editar margen',
    },
    yup: {
      nameRequired: 'El nombre es requerido',
      nameCharLength: 'El nombre de la regla puede tener 50 caracteres o menos',
      operatorRequired: 'El operador es requerido',
      operatorInvalid: 'Operador invalido',
      fieldRequired: 'Campo requerido',
      fieldInvalid: 'Campo invalido',
      valueRequired: 'El valor es requerido',
      valueCharLength: 'El valor puede tener 100 caracteres o menos',
      minConditions: 'Necesitas una al menos una condicion',
      markupRequired: 'El Margen es requerido',
      valueNotNegative: 'El valor no puede ser negativo',
    },
    hotelMarkupFieldOptions: {
      [MarkupFieldOptionHotels.PAX_QTY]: 'Cantidad de pasajeros',
      [MarkupFieldOptionHotels.ROOM_QTY]: 'Cantidad de habitaciones',
      [MarkupFieldOptionHotels.HOTEL_VENDOR]: 'Cadena hotelera',
      [MarkupFieldOptionHotels.REQUEST_DATE]: 'Fecha de solicitud',
      [MarkupFieldOptionHotels.TRIP_START_DATE]: 'Fecha de inicio del viaje',
      [MarkupFieldOptionHotels.PROPERTY_NAME]: 'Nombre de la propiedad',
      [MarkupFieldOptionHotels.DESTINATION]: 'Destino',
      [MarkupFieldOptionHotels.HAS_INFANT]: 'Tiene infantes',
      [MarkupFieldOptionHotels.HAS_CHILD]: 'Tiene niños',
      [MarkupFieldOptionHotels.RATE_CURRENCY]: 'Moneda',
      [MarkupFieldOptionHotels.RATE_REFUNDABLE]: 'Es reembolsable',
      [MarkupFieldOptionHotels.IS_CORPO]: 'Es corporativo',
      [MarkupFieldOptionHotels.CONNECTIVITY_VENDOR]: 'Proveedor de conectividad',
    },
    operators: {
      [MarkupRuleOperator.EQUALS]: '=',
      [MarkupRuleOperator.NOT_EQUALS]: '!=',
      [MarkupRuleOperator.GREATER_THAN]: '>',
      [MarkupRuleOperator.GREATER_THAN_OR_EQUAL]: '>=',
      [MarkupRuleOperator.LESS_THAN]: '<',
      [MarkupRuleOperator.LESS_THAN_OR_EQUAL]: '=<',
      [MarkupRuleOperator.CONTAINS]: 'contiene',
      [MarkupRuleOperator.NOT_CONTAINS]: 'no contiene',
      [MarkupRuleOperator.STARTS_WITH]: 'empieza con',
      [MarkupRuleOperator.ENDS_WITH]: 'ends termina con',
    },
  },
  cart: {
    title: 'Tu Carrito',
    summary: 'Resumen',
    total: 'Total',
    checkout: 'Crear a Orden de Venta',
    keepBuying: 'Seguir Comprando',
    cartAddItemError: 'Hubo un error al intentar agregar el item al carrito',
    hotels: 'Hoteles',
    cars: 'Autos',
    flights: 'Vuelos',
    services: 'Servicios',
    convert: {
      success: 'Orden de venta creada',
      error: 'Hubo un error al convertir el carrito a una orden de venta. Por favor intentalo más tarde',
      successMessage: 'El número de seguimieton de la orden es:',
    },
  },
  saleOrder: {
    status: {
      [SALE_ORDER_STATUS.Sold]: 'Vendido',
      [SALE_ORDER_STATUS.Active]: 'Activo',
      [SALE_ORDER_STATUS.Canceled]: 'Cancelado',
      [SALE_ORDER_STATUS.Lead]: 'Lead',
    },
    list: {
      title: 'Órdenes de venta',
      subtitle: 'Lista de órdenes de venta',
      results: 'resultados',
      table: {
        id: '#',
        status: 'Estado',
        createdBy: 'Autor',
        convertedAt: 'Convertido',
        updatedAt: 'Actualizado',
        case: 'Caso',
        amount: {
          title: 'Monto',
          value: 'USD {{value}}',
        },
      },
    },
    view: {
      title: 'Orden de venta',
      subtitle: '# {{record}}',
      sideBarTitle: 'Menú lateral de orden de venta',
      passengers: {
        title: 'Pasajeros',
        subtitle: 'Lista de pasajeros',
        newPassenger: 'Nuevo Pasajero',
        passengerData: 'Datos del Pasajero',
        successUpdatingPax: 'Información del pasajero actualizada exitosamente',
        errorUpdatingPax: 'Error al actualizar la información del pasajero',
        table: {
          name: 'Nombre',
          lastname: 'Apellido',
          email: 'Email',
          age: 'Edad (actual)',
          dob: 'Fecha de nacimiento',
          actions: 'Acciones',
        },
        form: {
          name: 'Nombre',
          lastName: 'Apellido',
          birthDate: 'Fecha de Nacimiento',
          email: 'Email',
          phone: 'Teléfono',
        },
      },
      hotels: {
        title: 'Hotel: {{name}}',
        detail: {
          title: 'Detalles',
          nights: 'Noches',
          markup: 'Markup %',
          subPrice: 'Sub precio',
          totalPrice: 'Precio total',
          checkIn: 'Check In',
          checkOut: 'Check Out',
          bookingId: 'ID de reserva',
          supplierReference: 'Referencia - {{supplierName}}',
          board: 'Régimen',
          cancelPolicies: 'Políticas de cancelación',
          remarks: 'Observaciones',
          rateRules: 'Reglas de tarifa',
        },
        cancel: 'Cancelar',
        cancelled: 'Cancelado',
        cancelSuccess: 'Reserva cancelada exitosamente',
        emit: 'Emitir',
        emitSuccess: 'Reserva emitida exitosamente',
        emitError: 'Hubo un error al emitir la reserva',
        holder: {
          title: 'Datos del titular',
          name: 'Nombre',
          lastname: 'Apellido',
          email: 'Email',
        },
      },
      rooms: {
        title: 'Habitaciones ({{rooms}})',
        paxName: 'Nombre',
        paxAge: 'Edad (al momento del check-out)',
      },
    },
    link: {
      action: {
        add: 'Agregar orden de venta',
      },
      modal: {
        action: 'Vincular',
        title: 'Vincular a caso',
        searchPlaceholder: 'Buscar por número de orden...',
        loadError: 'Error al cargar órdenes de venta',
      },
      list: {
        title: 'Órdenes de Venta',
        linkButton: 'Vincular Orden',
        createdBy: 'Creado por',
        amount: 'Monto',
        name: 'Nombre',
        unlinkConfirm: {
          title: 'Confirmar Desvincular',
          message: '¿Estás seguro que deseas desvincular esta orden?',
          cancel: 'Cancelar',
          confirm: 'Desvincular',
        },
      },
      notifications: {
        linkSuccess: 'Orden de venta vinculada exitosamente',
        linkError: 'Error al vincular orden de venta',
        unlinkSuccess: 'Orden de venta desvinculada exitosamente',
        unlinkError: 'Error al desvincular orden de venta',
        loadError: 'Error al cargar órdenes de venta',
      },
    },
    unlink: {
      modal: {
        title: 'Confirmar Desvincular',
        message: '¿Estás seguro que deseas desvincular esta orden?',
        cancel: 'Cancelar',
        confirm: 'Desvincular',
      },
      notifications: {
        unlinkSuccess: 'Orden de venta desvinculada exitosamente',
        unlinkError: 'Error al desvincular orden de venta',
      },
    },
    bookingDetails: {
      title: 'Detalles del proveedor',
      hotelName: 'Nombre de hotel proveedor',
      provider: 'Nombre del proveedor',
      reference: 'Referencia de reserva del proveedor',
    },
  },
  invalidationRules: {
    name: 'Nombre',
    expireAfter: 'Expira en',
    isActive: 'Activo',
    title: 'Reglas de Invalidación',
    subtitle: 'Listado de reglas de invalidación',
    results: 'Resultados',
    search: 'Buscar',
    minutes: 'Minutos',
    serviceType: {
      hotels: 'Hoteles',
    },
    table: {
      order: 'Orden',
      isDefault: 'Por defecto',
      serviceType: 'Tipo de servicio',
      yes: 'Sí',
      no: 'No',
      lastUpdate: 'Última actualización',
      created: 'Creado',
      updatedBy: 'Actualizado por',
      createdBy: 'Creado por',
    },
    validations: {
      nameCharLength: 'El nombre de la regla debe tener 255 caracteres o menos',
      fieldRequired: 'Este campo es obligatorio',
      expireAfterMin: 'El valor de expiración debe ser mayor o igual a 0',
      expireAfterMustBeNumber: 'El valor de expiración debe ser un número',
      operatorInvalid: 'Operador inválido seleccionado',
      fieldInvalid: 'Campo inválido seleccionado',
      valueRequired: 'El valor es obligatorio',
      valueCharLength: 'El valor debe tener 255 caracteres o menos',
      minConditions: 'Se requiere al menos una condición',
      valueInvalidDate: 'Fecha inválida, el valor debe tener el formato YYYY-MM-DD',
    },
    viewEdit: {
      titleNew: 'Crear regla de invalidación',
      titleEdit: 'Editar regla de invalidación',
      subtitleNew: 'Crear regla de invalidación',
      subtitleEdit: 'Editar regla de invalidación',
      deleteGroup: 'Eliminar grupo',
      addCondition: '+ Agregar condición',
      addAction: '+ Agregar acción',
      createSuccess: 'Regla de invalidación creada',
      createError: 'Ocurrió un error al crear la regla de invalidación',
      updateSuccess: 'Regla de invalidación actualizada',
      updateError: 'Ocurrió un error al actualizar la regla de invalidación',
      buttonDiscard: 'Descartar',
      conditions: 'Condiciones',
      field: 'Campo',
      operator: 'Operador',
      value: 'Valor',
    },
    fieldOptions: {
      SUPPLIER_ACCESS_CODE: 'Código de acceso del proveedor',
      CHECK_IN_DATE: 'Fecha de check-in',
      CHECK_OUT_DATE: 'Fecha de check-out',
    },
    operatorOptions: {
      EQUALS: '=',
      NOT_EQUALS: '!=',
      GREATER_THAN: '>',
      GREATER_THAN_OR_EQUAL: '>=',
      LESS_THAN: '<',
      LESS_THAN_OR_EQUAL: '=<',
    },
  },
  dataUploads: {
    title: 'Cargas de Datos',
    subtitle: 'Carga masiva de datos',
    tabs: {
      clients: 'Clientes',
      cases: 'Casos',
    },
    clients: {
      title: 'Carga de Clientes',
      subtitle: 'Carga masiva de clientes',
      results: 'resultados',
      search: 'Buscar',
      table: {
        id: 'ID',
        agencyId: 'ID de Agencia',
        status: 'Estado',
        createdAt: 'Fecha de Creación',
        createdBy: 'Creado Por',
        filename: 'Nombre del Archivo',
        actions: 'Descargar',
      },
      status: {
        created: 'Creado',
        pending: 'Pendiente',
        processing: 'Procesando',
        completed: 'Completado',
        failed: 'Fallido',
      },
      create: {
        title: 'Subir archivo de clientes',
        description: 'Seleccione un archivo CSV con datos de clientes',
        createdSuccess: 'Archivo subido exitosamente',
        createdError: 'Hubo un error al subir el archivo',
      },
    },
    cases: {
      title: 'Carga de Casos',
      subtitle: 'Carga masiva de casos',
      results: 'resultados',
      search: 'Buscar',
      table: {
        id: 'ID',
        agencyId: 'ID de Agencia',
        status: 'Estado',
        createdAt: 'Fecha de Creación',
        createdBy: 'Creado Por',
        filename: 'Nombre del Archivo',
        workflow: 'Workflow',
        actions: 'Descargar',
      },
      status: {
        created: 'Creado',
        pending: 'Pendiente',
        processing: 'Procesando',
        completed: 'Completado',
        failed: 'Fallido',
      },
      create: {
        title: 'Subir archivo de casos',
        description: 'Seleccione un archivo CSV con datos de casos',
        createdSuccess: 'Archivo subido exitosamente',
        createdError: 'Hubo un error al subir el archivo',
      },
    },
  },
  flightIntegrations: {
    title: 'Integraciones de Vuelo',
    subtitle: 'Lista de integraciones de vuelo',
    results: 'Resultados',
    search: 'Buscar',
    table: {
      provider: 'Proveedor',
      ownerClientName: 'Cliente propietario',
      isShared: 'Es compartida',
      createdAt: 'Creado en',
    },
    create: {
      title: 'Crear Integración de Vuelo',
      success: 'Integración de vuelo creada exitosamente',
      error: 'Error al crear la integración de vuelo',
    },
    edit: {
      loadItemError: 'Error al cargar integración de vuelo',
      title: 'Editar Integración de Vuelo',
      success: 'Integración de vuelo actualizada exitosamente',
      error: 'Error al actualizar la integración de vuelo',
    },
    update: {
      success: 'Integración de vuelo actualizada exitosamente',
      error: 'Error al actualizar la integración de vuelo',
    },
    form: {
      provider: {
        label: 'Proveedor',
      },
      allowSeeByOthers: {
        label: 'Permitir ver por otros clientes',
      },
      username: {
        label: 'Usuario',
      },
      password: {
        label: 'Contraseña',
      },
    },
  },
  hotelIntegrations: {
    title: 'Integraciones de Hotel',
    subtitle: 'Lista de integraciones de hotel',
    results: 'Resultados',
    search: 'Buscar',
    table: {
      name: 'Nombre',
      provider: 'Proveedor',
      ownerClientName: 'Cliente propietario',
      isShared: 'Es compartida',
      createdAt: 'Creado en',
    },
    create: {
      title: 'Crear Integración de Hotel',
      success: 'Integración de hotel creada exitosamente',
      error: 'Error al crear la integración de hotel',
    },
    edit: {
      loadItemError: 'Error al cargar integración de hotel',
      title: 'Editar Integración de Hotel',
      success: 'Integración de hotel actualizada exitosamente',
      error: 'Error al actualizar la integración de hotel',
    },
    update: {
      success: 'Integración de hotel actualizada exitosamente',
      error: 'Error al actualizar la integración de hotel',
    },
    form: {
      provider: {
        label: 'Proveedor',
      },
      allowSeeByOthers: {
        label: 'Permitir ver por otros clientes',
      },
      apiKey: {
        label: 'Clave API',
      },
      clientCode: {
        label: 'Código de Cliente',
      },
      clientName: {
        label: 'Nombre del Cliente',
      },
      name: {
        label: 'Nombre de la Integración',
      },
      suppliers: {
        label: 'Proveedores',
        helperText: 'Selecciona los proveedores para esta integración',
      },
    },
  },
  agencyFlightIntegrations: {
    title: 'Integraciones de vuelos por agencia',
    subtitle: 'Listado de integraciones de vuelos de la agencia',
    results: 'Resultados',
    search: 'Buscar',
    table: {
      provider: 'Proveedor',
      ownerClientName: 'Cliente propietario',
      isShared: 'Es compartida',
      createdAt: 'Fecha de creación',
      isEnabledForAgency: 'Integración habilitada',
      itemEnabled: 'La integración está habilitada',
      itemDisabled: 'Esta integración está deshabilitada',
    },
    enabled: {
      success: 'La integración de vuelos se habilitó correctamente.',
      error: 'Ocurrió un error al habilitar la integración de vuelos. Por favor, intentá nuevamente.',
    },
    disabled: {
      success: 'La integración de vuelos se deshabilitó correctamente.',
      error: 'Ocurrió un error al deshabilitar la integración de vuelos. Por favor, intentá nuevamente.',
    },
    confirmDisableModal: {
      title: 'Deshabilitar integración de vuelos',
      message:
        'Si deshabilitás esta integración de vuelos, la agencia ya no podrá buscar vuelos usando esta integración. ¿Querés continuar de todas formas?',
      accept: 'Aceptar',
      cancel: 'Cancelar',
    },
  },
  hotelContent: {
    management: {
      title: 'Gestión de Contenido Hotelero',
      general: 'General',
      activeJobs: 'Trabajos Activos',
      viewJobs: 'Ver trabajos',
      tabs: {
        suppliers: 'Proveedores',
        defaultSuppliers: 'Proveedores Predeterminados',
        boards: 'Tableros',
        categories: 'Categorías',
        destinations: 'Destinos',
        vendorHotels: 'Hoteles de Proveedores',
        nimbuxHotels: 'Hoteles Nimbux',
      },
      defaultSuppliers: {
        title: 'Proveedores Predeterminados',
        subtitle: 'Configura el orden de prioridad de los proveedores para cada tipo de contenido',
      },
      suppliers: {
        title: 'Proveedores de Contenido Hotelero',
        subtitle: 'Gestionar y visualizar proveedores de contenido hotelero',
        updateMetadata: 'Actualizar Metadata',
        updateMetadataMessages: {
          success: 'Metadata actualizada exitosamente',
          error: 'Error al actualizar metadata',
        },
        refreshMessages: {
          success: 'Se encolo un Job para actualizar los proveedores',
          error: 'Error al actualizar los proveedores',
        },
        table: {
          supplier: 'Proveedor',
          provider: 'Proveedor',
          supplierCode: 'Código de Proveedor',
          providerCode: 'Código de Proveedor',
          supplierName: 'Nombre del Proveedor',
          supplierContext: 'Contexto del Proveedor',
          providerName: 'Nombre del Proveedor',
          isProviderActive: 'Proveedor Activo',
          isSupplierActive: 'Proveedor Activo',
        },
        errors: {
          getSuppliers: 'Error al cargar proveedores',
        },
      },
      vendorHotels: {
        title: 'Hoteles de Proveedores',
        subtitle: 'Gestionar y visualizar listados de hoteles de proveedores',
        createExtraction: 'Crear Extracción',
        table: {
          name: 'Nombre del Hotel',
          code: 'Código del Hotel',
          location: 'Ubicación',
          address: 'Dirección',
          supplier: 'Proveedor',
          parent: 'Hotel Padre',
        },
        clearFilters: 'Limpiar Filtros',
        linkModal: {
          currentNimbuxHotel: 'Nimbux Hotel Actual',
          linkToNimbuxHotel: 'Vincular a Nimbux Hotel',
          searchPlaceholder: 'Buscar Nimbux Hotels...',
          availableNimbuxHotels: 'Nimbux Hotels Disponibles',
          noNimbuxHotelsFound: 'No se encontraron Nimbux Hotels',
          noLinkedNimbuxHotel: 'Este hotel no está vinculado a ningún Nimbux Hotel',
          link: 'Vincular',
          unlink: 'Desvincular',
          nimbuxHotelLabel: 'Nimbux Hotel:',
          noNimbuxHotel: 'No tiene',
        },
        errors: {
          getVendorHotels: 'Error al cargar hoteles de proveedores',
        },
        modal: {
          title: 'Crear Nueva Extracción',
          supplier: 'Proveedor',
          destination: 'Destino',
          startExtraction: 'Iniciar Extracción',
        },
        moderate: {
          title: 'Moderar',
          success: 'Hoteles moderados exitosamente',
          error: 'Error al moderar los hoteles',
        },
      },
      boards: {
        title: 'Regímenes',
        subtitle: 'Gestionar y visualizar regímenes',
        table: {
          code: 'Código',
          supplierName: 'Nombre del Proveedor',
          texts: 'Textos',
        },
        errors: {
          getBoards: 'Error al cargar regímenes',
        },
      },
      categories: {
        title: 'Categorías',
        subtitle: 'Gestionar y visualizar categorías',
        table: {
          code: 'Código',
          supplierName: 'Nombre del Proveedor',
          texts: 'Textos',
        },
        errors: {
          getCategories: 'Error al cargar categorías',
        },
      },
      amenties: {
        title: 'Amenities',
        subtitle: 'Gestionar y visualizar amenities',
        table: {
          code: 'Código',
          supplierName: 'Nombre del Proveedor',
          texts: 'Textos',
        },
        errors: {
          getAmenities: 'Error al cargar amenities',
        },
      },
      destinations: {
        title: 'Destinos',
        subtitle: 'Gestionar y visualizar destinos',
        table: {
          code: 'Código',
          supplierName: 'Nombre del Proveedor',
          texts: 'Textos',
          type: 'Tipo',
        },
        errors: {
          getDestinations: 'Error al cargar destinos',
        },
      },
    },
    extraction: {
      supplier: 'Proveedor',
      destinations: 'Destinos',
      success: 'Extracción iniciada exitosamente',
      error: 'Error al iniciar la extracción',
      newExtraction: 'Crear Nueva Extracción',
      startExtraction: 'Iniciar Extracción',
    },
    jobs: {
      errors: {
        getJobs: 'Error al cargar trabajos de extracción de contenido',
      },
      modal: {
        title: 'Colas de Extracción',
        resultsFound: 'trabajos encontrados',
        noJobsFound: 'No se encontraron trabajos',
        parameters: 'Parámetros',
        errors: 'Errores',
        created: 'Creado',
        started: 'Iniciado',
        finished: 'Finalizado',
        status: {
          pending: 'Pendiente',
          processing: 'En progreso',
          completed: 'Completado',
          failed: 'Fallido',
        },
      },
    },
    suppliers: 'Proveedores',
    boards: 'Regímenes',
    categories: 'Categorías',
    destinations: 'Destinos',
    vendorHotels: 'Hoteles del Proveedor',
    nimbuxHotels: {
      title: 'Hoteles Nimbux',
      empty: 'Aquí se mostrará la lista de hoteles',
      emptyDescription: 'Esta sección contendrá la interfaz de gestión para hoteles Nimbux.',
      card: {
        information: 'Información de la zona',
        lastModeration: 'Última moderación fue: {{date}}',
        editButton: 'Editar',
        link: 'Link',
        opinions: '({{count}} opiniones)',
        popularAmenities: 'Comodidades populares',
        showAllAmenities: 'Ver todas las comodidades',
        showLessAmenities: 'Mostrar menos comodidades',
        status: {
          markedForModeration: 'Marcado para moderar',
          moderated: 'Moderado',
          toReview: 'Para revisar',
          reviewed: 'Revisado',
        },
      },
      linkModal: {
        title: 'Vincular Hoteles - {{hotelName}}',
        searchPlaceholder: 'Buscar hoteles...',
        linkedHotels: 'Hoteles Vinculados',
        availableHotels: 'Hoteles Disponibles',
        noLinkedHotels: 'No hay hoteles vinculados aún',
        noAvailableHotels: 'No se encontraron hoteles',
        link: 'Vincular',
        unlink: 'Desvincular',
        country: 'País',
        city: 'Ciudad',
        parentHotel: 'Hotel Nimbux',
        noParentHotel: 'Sin hotel nimbux',
        noSupplier: 'Sin proveedor',
        close: 'Cerrar',
      },
      errors: {
        getNimbuxHotels: 'Error al cargar hoteles nimbux',
        loadingError: 'Error al cargar los hoteles. Por favor, intenta de nuevo.',
      },
      loadingMore: 'Cargando más hoteles...',
      noMoreHotels: 'No hay más hoteles para mostrar',
    },
    filters: {
      orderBy: 'Ordenar por',
      name: 'Nombre',
      country: 'País',
      suppliers: 'Proveedores',
      searchByName: 'Buscar por Nombre',
      searchPlaceholder: 'Ingrese el nombre del hotel',
      filterByCountry: 'Filtrar por País',
      selectCountry: 'Seleccionar país',
      allCountries: 'Todos los países',
      filterByAddress: 'Filtrar por Dirección',
      address: 'Dirección',
      addressPlaceholder: 'Ingrese una dirección',
      distance: 'Distancia',
    },
    view: {
      tabs: {
        general: 'General',
        checkinCheckout: 'Check-in / Check-out',
        location: 'Ubicación',
        images: 'Imágenes',
        amenities: 'Amenities',
        content: 'Contenido',
      },
      images: {
        published: 'Publicadas ({{count}})',
        available: 'Disponibles ({{count}})',
        count: '{{count}} imágenes',
        mainBadge: 'Principal',
        moveToAvailable: 'Pasar a Disponibles',
        moveToPublished: 'Pasar a Publicadas',
        messages: {
          getVendorImagesError: 'Error al cargar imágenes',
          noAvailableImages: 'No hay imágenes disponibles',
          updateSuccess: 'Imágenes actualizadas exitosamente',
          updateError: 'Error al actualizar las imágenes',
        },
      },
      location: {
        title: 'Ubicación',
        address: 'Dirección',
        latitude: 'Latitud',
        longitude: 'Longitud',
        map: 'Mapa',
        dragInstructions: 'Arrastra el pin en el mapa para ajustar la ubicación manualmente',
        loadingMap: 'Cargando mapa...',
        messages: {
          updateSuccess: 'Ubicación actualizada exitosamente',
          updateError: 'Error al actualizar la ubicación',
        },
      },
      amenities: {
        title: 'Amenities',
        messages: {
          updateSuccess: 'Amenities actualizadas exitosamente',
          updateError: 'Error al actualizar las amenities',
        },
        selectedAmenities: 'Amenities seleccionadas ({{count}})',
      },
      checkinCheckout: {
        title: 'Check-in / Check-out',
        checkIn: 'Check-in',
        checkOut: 'Check-out',
        from: 'Desde',
        to: 'Hasta',
        minimumAge: 'Edad mínima',
        minimumAgeForCheckin: 'Edad mínima para el checkin',
        minimumAgeForCheckout: 'Edad mínima para el checkout',
        selectTime: 'Seleccionar hora',
        selectAge: 'Seleccionar edad',
        years: 'años',
        instructionsForCheckin: 'Instrucciones para el checkin',
        instructionsForCheckout: 'Instrucciones para el checkout',
        generateWithAI: 'Completar con IA',
        messages: {
          updateSuccess: 'Check-in / Check-out actualizado correctamente',
          updateError: 'Error al actualizar el check-in / check-out',
          generateTranslationsSuccess: 'Translations generated successfully',
          generateTranslationsError: 'Error al generar las traducciones',
        },
      },
      areaInformation: 'Información de la zona',
    },
    contentEditor: {
      title: 'Contenido Nimbux',
      editorTitle: 'Editor de Contenido',
      placeholder: 'Edita el contenido del hotel aquí',
      suppliersPreview: 'Descripción Suppliers (Preview)',
      generateWithAI: 'Generar con IA',
      ckeditor: {
        paragraph: 'Párrafo',
        heading1: 'Título 1',
        heading2: 'Título 2',
        heading3: 'Título 3',
        heading4: 'Título 4',
      },
      expandContent: 'Expandir contenido',
      collapseContent: 'Colapsar contenido',
      noDescriptionsAvailable: 'No hay descripciones disponibles para este proveedor',
      loadingDescriptions: 'Cargando descripciones de proveedores...',
      noDescriptionsFromSuppliers: 'No hay descripciones de proveedores disponibles',
    },
  },
  caseTriggers: {
    form: {
      sendWhatsapp: 'Enviar mensaje de WhatsApp',
      statusPlaceholderAny: 'Cualquier estado (déjalo vacío para aplicar a todos)',
      statusHelperAny: 'Si no seleccionas ningún estado, este disparador se aplicará sin importar el estado del caso.',
      nextStatus: {
        keepCurrent: 'Mantener estado actual',
      },
    },
    template: {
      placeholdersHint:
        'Puedes usar marcadores dinámicos como {{client_name}}, {{client_first_name}}, {{client_last_name}}, {{agent_name}}, {{agent_first_name}}, {{agent_last_name}}; se reemplazan automáticamente por los datos del cliente en cada caso.',
      supportedPlaceholders:
        'Marcadores disponibles: {{client_name}}, {{client_first_name}}, {{client_last_name}}, {{agent_name}}, {{agent_first_name}}, {{agent_last_name}}',
    },
  },
};
